// src/contexts/FirebaseContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  collection,
  doc,
  getDocs,
  getCountFromServer,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  orderBy,
  where,
  writeBatch,
  documentId,
  arrayUnion,
  increment,
  collectionGroup
} from 'firebase/firestore'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage'
import {
  ref as dbRef,
  set,
  push,
  get,
  update as rtdbUpdate,
  remove
} from 'firebase/database'
import { db, storage, database, auth, getFirestoreInstance } from '../config/firebase'

const FirebaseContext = createContext()

const useFirebase = () => {
  const context = useContext(FirebaseContext)
  if (!context) throw new Error('useFirebase must be used within a FirebaseProvider')
  return context
}

const FirebaseProvider = ({ children }) => {
  const [testCache, setTestCache] = useState(new Map())

  useEffect(() => () => setTestCache(new Map()), [])
  useEffect(() => console.log('🧪 Test cache updated:', Array.from(testCache.entries())), [testCache])

  // Log Firebase initialization status
  useEffect(() => {
    console.log('🔧 FirebaseProvider: Checking Firebase instances...', {
      db: !!db,
      storage: !!storage,
      auth: !!auth,
      database: !!database
    })
  }, [])

  // Utility function to safely convert Firestore timestamps to JavaScript Date objects
  // Make functions globally accessible for debugging in browser console
  useEffect(() => {
    // Only in development mode and after Firebase is initialized
    if (import.meta.env.DEV) {
      // Add a small delay to ensure Firebase initialization completes
      const initDebug = () => {
        try {
          window.debugFirebase = {
            testFirestoreConnection,
            addAlertSubnote,
            getAlertSubnotes,
            testSubnoteDataIntegrity,
            db: db || null,
            storage: storage || null,
            auth: auth || null
          }
          console.log('🔧 Debug: Firebase functions available in window.debugFirebase')
          console.log('🔧 Debug: Try window.debugFirebase.testFirestoreConnection()')
          console.log('🔧 Debug: Try window.debugFirebase.addAlertSubnote("your-alert-id", {title: "test", message: "test message"})')
        } catch (error) {
          console.warn('⚠️ Debug setup error:', error.message)
        }
      }

      // Initialize debug after a short delay
      setTimeout(initDebug, 1000)
    }
  }, [])

  const convertFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return null
    if (timestamp instanceof Date) return timestamp
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate()
    }
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp)
    }
    return null
  }

  // Utility function to validate and structure subnote data
  const validateSubnoteData = (subnote) => {
    if (!subnote || typeof subnote !== 'object') {
      throw new Error('Invalid subnote data: must be an object')
    }

    const { title = '', message = '' } = subnote

    // Ensure we have some content
    if (!title.trim() && !message.trim()) {
      throw new Error('Subnote must have either title or message content')
    }

    // Start with base structure
    const validatedData = {
      title: title.trim(),
      message: message.trim(),
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: subnote.createdBy || 'admin@nursingexam.com'
    }

    // Add file-related fields if they exist
    if (subnote.fileUrl) validatedData.fileUrl = subnote.fileUrl
    if (subnote.fileName) validatedData.fileName = subnote.fileName
    if (subnote.fileType) validatedData.fileType = subnote.fileType

    // Handle boolean flags properly
    if (subnote.hasOwnProperty('isFileContent')) {
      validatedData.isFileContent = Boolean(subnote.isFileContent)
    }
    if (subnote.hasOwnProperty('isWordFile')) {
      validatedData.isWordFile = Boolean(subnote.isWordFile)
    }

    // Preserve any additional metadata that might be important
    const preservedFields = ['category', 'tags', 'priority', 'metadata']
    preservedFields.forEach(field => {
      if (subnote.hasOwnProperty(field)) {
        validatedData[field] = subnote[field]
      }
    })

    console.log('🔍 Validated subnote data structure:', {
      hasTitle: !!validatedData.title,
      hasMessage: !!validatedData.message,
      hasFileUrl: !!validatedData.fileUrl,
      isFileContent: validatedData.isFileContent,
      additionalFields: Object.keys(validatedData).filter(key =>
        !['title', 'message', 'createdAt', 'updatedAt', 'createdBy'].includes(key)
      )
    })

    return validatedData
  }

  // Utility function to calculate document size in bytes
  const getDocumentSizeInBytes = (data) => {
    return new Blob([JSON.stringify(data)]).size
  }

  // Constants for Firestore limits
  // Note: Firestore hard limit is 1MB, but we set slightly lower for safety
  const FIRESTORE_MAX_DOCUMENT_SIZE = 1048000 // ~1MB (Firestore's absolute maximum)

  // Utility function to compress base64 images in HTML content
  const compressImagesInContent = async (htmlContent, maxWidth = 600, quality = 0.5) => {
    if (!htmlContent || !htmlContent.includes('data:image')) {
      return htmlContent
    }

    let compressedContent = htmlContent
    const imageRegex = /<img[^>]*src=["']data:image\/([^;]+);base64,([^"']*?)["'][^>]*>/gi
    const images = [...htmlContent.matchAll(imageRegex)]

    for (const [fullMatch, format, base64Data] of images) {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()

        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = `data:image/${format};base64,${base64Data}`
        })

        // Calculate new dimensions
        const scale = Math.min(maxWidth / img.width, maxWidth / img.height, 1)
        canvas.width = img.width * scale
        canvas.height = img.height * scale

        // Draw and compress
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const compressedDataUrl = canvas.toDataURL(`image/${format}`, quality)

        // Replace in content
        compressedContent = compressedContent.replace(fullMatch,
          fullMatch.replace(`data:image/${format};base64,${base64Data}`, compressedDataUrl))
      } catch (error) {
        console.warn('Failed to compress image, keeping original:', error)
      }
    }

    return compressedContent
  }

  const getAlertSubnotes = async (alertId) => {
    try {
      console.log('📖 Starting to fetch subnotes...', {
        alertId,
        timestamp: new Date().toISOString()
      })

      if (!alertId) {
        console.warn('⚠️ No alertId provided to getAlertSubnotes')
        return []
      }

      if (!db) {
        console.error('❌ Firestore database not initialized in getAlertSubnotes')
        throw new Error('Firestore database not initialized')
      }

      console.log('🔗 Firestore connection status in getAlertSubnotes:', {
        db: !!db,
        type: typeof db
      })

      const subRef = collection(db, 'alerts', alertId, 'subnotes')
      console.log('📍 Collection reference created for path:', `alerts/${alertId}/subnotes`)

      console.log('🔍 Executing Firestore query...')
      const snap = await getDocs(query(subRef, orderBy('createdAt', 'desc')))

      console.log('📊 Query completed. Raw results:', {
        docsCount: snap.size,
        empty: snap.empty,
        metadata: {
          fromCache: snap.metadata?.fromCache,
          hasPendingWrites: snap.metadata?.hasPendingWrites
        }
      })

      const subnotes = []

      for (const doc of snap.docs) {
        const data = doc.data()
        console.log('📄 Processing document:', {
          id: doc.id,
          hasTitle: !!data.title,
          hasMessage: !!data.message,
          messageLength: data.message?.length,
          createdAt: data.createdAt,
          isFileContent: data.isFileContent
        })

        let processedData = {
          id: doc.id,
          ...data,
          // Ensure timestamps are properly handled
          createdAt: convertFirestoreTimestamp(data.createdAt),
          updatedAt: convertFirestoreTimestamp(data.updatedAt)
        }

        subnotes.push(processedData)
      }

      console.log('✅ Successfully processed and fetched subnotes:', {
        alertId,
        count: subnotes.length,
        path: `alerts/${alertId}/subnotes`,
        subnoteIds: subnotes.map(s => s.id)
      })

      return subnotes
    } catch (error) {
      console.error('❌ COMPLETE ERROR in getAlertSubnotes:', {
        alertId,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      })
      return []
    }
  }

  const updateAlertSubnote = async (alertId, subnoteId, data) => {
    try {
      if (!alertId || !subnoteId) throw new Error('alertId and subnoteId are required')
      if (!data || Object.keys(data).length === 0) throw new Error('Update data is required')

      console.log('🔄 Updating subnote:', { alertId, subnoteId, fields: Object.keys(data) })

      const subDocRef = doc(db, 'alerts', alertId, 'subnotes', subnoteId)

      // Check for old file to delete from Storage if being replaced
      if (data.fileUrl) {
        try {
          const currentDoc = await getDoc(subDocRef)
          if (currentDoc.exists()) {
            const currentData = currentDoc.data()
            const oldFileUrl = currentData.fileUrl

            // Delete old file if it exists and is different from new one
            if (oldFileUrl && oldFileUrl !== data.fileUrl && oldFileUrl.includes('firebase')) {
              console.log('🗑️ Deleting old file from Storage:', oldFileUrl)
              try {
                await deleteImage(oldFileUrl)
                console.log('✅ Old file deleted from Storage')
              } catch (deleteError) {
                console.warn('⚠️ Could not delete old file (may not exist):', deleteError.message)
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ Could not check for old file:', error.message)
        }
      }

      // Check if update will make document too large
      if (data.message) {
        const currentDoc = await getDoc(subDocRef)
        const currentData = currentDoc.data()
        const newSize = getDocumentSizeInBytes({ ...currentData, ...data })

        if (newSize > FIRESTORE_MAX_DOCUMENT_SIZE) {
          // Try compression first if content has images
          if (data.message.includes('<img')) {
            try {
              const compressedMessage = await compressImagesInContent(data.message)
              const compressedSize = getDocumentSizeInBytes({ ...currentData, message: compressedMessage })

              if (compressedSize <= FIRESTORE_MAX_DOCUMENT_SIZE) {
                data.message = compressedMessage
                data.wasCompressed = true
              } else {
                throw new Error(`Document too large even after compression (${compressedSize} bytes). Maximum allowed: ${FIRESTORE_MAX_DOCUMENT_SIZE} bytes. Please reduce file size.`)
              }
            } catch (compressionError) {
              console.warn('⚠️ Compression failed:', compressionError)
              throw new Error(`Document too large (${newSize} bytes). Maximum allowed: ${FIRESTORE_MAX_DOCUMENT_SIZE} bytes. Please reduce file size.`)
            }
          } else {
            throw new Error(`Document too large (${newSize} bytes). Maximum allowed: ${FIRESTORE_MAX_DOCUMENT_SIZE} bytes. Please reduce file size.`)
          }
        }
      }

      const updatePayload = {
        ...data,
        updatedAt: new Date(),
        updatedBy: data.updatedBy || 'admin@nursingexam.com'
      }

      await updateDoc(subDocRef, updatePayload)
      console.log('✅ Subnote updated successfully')
    } catch (error) {
      console.error('❌ Error updating alert subnote:', error)
      throw error
    }
  }

  const deleteAlertSubnote = async (alertId, subnoteId) => {
    try {
      if (!alertId || !subnoteId) throw new Error('alertId and subnoteId are required')

      console.log('🗑️ Deleting subnote:', { alertId, subnoteId })

      const subDocRef = doc(db, 'alerts', alertId, 'subnotes', subnoteId)

      // Get the document to check for Storage file
      const docSnap = await getDoc(subDocRef)
      if (docSnap.exists()) {
        const data = docSnap.data()
        const fileUrl = data.fileUrl

        // Delete file from Storage if it exists
        if (fileUrl && fileUrl.includes('firebase')) {
          console.log('🗑️ Deleting file from Storage:', fileUrl)
          try {
            await deleteImage(fileUrl)
            console.log('✅ File deleted from Storage')
          } catch (deleteError) {
            console.warn('⚠️ Could not delete file (may not exist):', deleteError.message)
            // Continue with document deletion even if file deletion fails
          }
        }
      }

      // Delete the Firestore document
      await deleteDoc(subDocRef)

      console.log('✅ Subnote deleted successfully')
    } catch (error) {
      console.error('❌ Error deleting alert subnote:', error)
      throw error
    }
  }

  // Test function to validate subnote data storage (for development/debugging)
  const testSubnoteDataIntegrity = async (alertId) => {
    if (!alertId) {
      console.error('❌ testSubnoteDataIntegrity: alertId is required')
      return false
    }

    try {
      console.log('🧪 Testing subnote data integrity for alert:', alertId)

      // First, test Firestore connection
      console.log('🔗 Testing Firestore connection...')
      if (!db) {
        console.error('❌ Firestore database is not initialized!')
        return false
      }

      // Try to read from Firestore to test connection
      try {
        const testRef = collection(db, 'alerts', alertId, 'subnotes')
        const existingSubnotes = await getDocs(testRef)
        console.log('✅ Firestore connection successful, existing subnotes:', existingSubnotes.size)
      } catch (connectionError) {
        console.error('❌ Firestore connection failed:', connectionError.message)
        return false
      }

      // Test 1: Simple text subnote
      console.log('🧪 Creating test text subnote...')
      const textSubnoteId = await addAlertSubnote(alertId, {
        title: 'Test Text Subnote',
        message: 'This is a test message for data integrity validation.'
      })
      console.log('✅ Text subnote created with ID:', textSubnoteId)

      // Test 2: File-based subnote
      console.log('🧪 Creating test file subnote...')
      const fileSubnoteId = await addAlertSubnote(alertId, {
        title: 'test-document.docx',
        message: '<p>This is converted HTML content from a Word document.</p>',
        fileUrl: 'https://example.com/test-file.docx',
        fileName: 'test-document.docx',
        fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        isFileContent: true,
        isWordFile: false
      })
      console.log('✅ File subnote created with ID:', fileSubnoteId)

      // Retrieve and validate data
      console.log('🔍 Retrieving all subnotes to validate...')
      const subnotes = await getAlertSubnotes(alertId)
      const textSubnote = subnotes.find(s => s.id === textSubnoteId)
      const fileSubnote = subnotes.find(s => s.id === fileSubnoteId)

      console.log('📋 Retrieved subnotes:', subnotes.map(s => ({ id: s.id, title: s.title })))

      // Validation checks
      const tests = [
        { name: 'Text subnote exists', passed: !!textSubnote },
        { name: 'File subnote exists', passed: !!fileSubnote },
        { name: 'Text subnote has correct title', passed: textSubnote?.title === 'Test Text Subnote' },
        { name: 'File subnote has fileUrl', passed: !!fileSubnote?.fileUrl },
        { name: 'File subnote has isFileContent flag', passed: fileSubnote?.isFileContent === true },
        { name: 'Both have createdAt timestamp', passed: !!textSubnote?.createdAt && !!fileSubnote?.createdAt },
        { name: 'Both have createdBy field', passed: !!textSubnote?.createdBy && !!fileSubnote?.createdBy }
      ]

      const allTestsPassed = tests.every(test => test.passed)

      console.log('🧪 Test Results:', tests)
      console.log(allTestsPassed ? '✅ All data integrity tests passed!' : '❌ Some tests failed')

      // Clean up test data
      try {
        console.log('🧹 Cleaning up test data...')
        await deleteAlertSubnote(alertId, textSubnoteId)
        await deleteAlertSubnote(alertId, fileSubnoteId)
        console.log('🧹 Test cleanup completed')
      } catch (cleanupError) {
        console.warn('⚠️ Failed to cleanup test data:', cleanupError.message)
      }

      return allTestsPassed
    } catch (error) {
      console.error('❌ Error during data integrity test:', error)
      return false
    }
  }

  // Debug function to test Firestore connectivity directly
  const testFirestoreConnection = async () => {
    try {
      console.log('🔗 Testing Firestore connection...')

      if (!db) {
        throw new Error('Firestore database is not initialized')
      }

      // Try to create a test document
      const testData = {
        test: true,
        timestamp: new Date(),
        message: 'Firestore connection test'
      }

      const testRef = collection(db, 'connection_test')
      const docRef = await addDoc(testRef, testData)

      console.log('✅ Test document created with ID:', docRef.id)

      // Try to read it back
      const readDoc = await getDoc(doc(db, 'connection_test', docRef.id))

      if (readDoc.exists()) {
        console.log('✅ Test document read successfully:', readDoc.data())

        // Clean up
        await deleteDoc(doc(db, 'connection_test', docRef.id))
        console.log('✅ Test document cleaned up')

        return true
      } else {
        console.error('❌ Test document not found after creation')
        return false
      }
    } catch (error) {
      console.error('❌ Firestore connection test failed:', error)
      return false
    }
  }


  // ----------------- Storage (Images / Files) -----------------
  const uploadImage = async (file, path = 'images') => {
    if (!file) throw new Error('No file provided to uploadImage')
    try {
      const imageRef = storageRef(storage, `images/${path}/${Date.now()}_${file.name}`)
      const snapshot = await uploadBytes(imageRef, file)
      return await getDownloadURL(snapshot.ref)
    } catch (error) {
      console.error('Error uploading image:', error)
      throw error
    }
  }

  const uploadFile = async (file, path = 'files') => {
    if (!file) throw new Error('No file provided to uploadFile')
    try {
      const fileRef = storageRef(storage, `${path}/${Date.now()}_${file.name}`)
      const snapshot = await uploadBytes(fileRef, file)
      return await getDownloadURL(snapshot.ref)
    } catch (error) {
      console.error('Error uploading file:', error)
      throw error
    }
  }

  // Add a subnote under Firestore path alerts/{alertId}/subnotes
  const addAlertSubnote = async (alertId, subnote) => {
    try {
      console.log('📝 Starting subnote creation process...', {
        alertId,
        subnoteKeys: Object.keys(subnote || {}),
        timestamp: new Date().toISOString()
      })

      if (!alertId) {
        console.error('❌ Missing alertId for creating subnote')
        throw new Error('alertId is required for creating subnote')
      }

      if (!db) {
        console.error('❌ Firestore database not initialized')
        throw new Error('Firestore database not initialized')
      }

      console.log('🔗 Firestore connection status:', {
        db: !!db,
        type: typeof db,
        app: !!db?._delegate?.app
      })

      // Use validation helper to ensure proper data structure
      console.log('🔍 Validating subnote data...')
      const validatedData = validateSubnoteData(subnote)
      console.log('✅ Data validated successfully:', {
        hasTitle: !!validatedData.title,
        hasMessage: !!validatedData.message,
        messageLength: validatedData.message?.length,
        hasFileUrl: !!validatedData.fileUrl
      })

      const subRef = collection(db, 'alerts', alertId, 'subnotes')
      console.log('📂 Collection reference created for path:', `alerts/${alertId}/subnotes`)

      // For file uploads, don't store HTML content in Firestore - only store metadata
      // The actual file is already in Firebase Storage (fileUrl)
      if (validatedData.isFileContent && validatedData.fileUrl) {
        console.log('📄 File-based subnote detected - storing only metadata')
        // Remove the message (HTML content) and only keep metadata
        validatedData.message = '' // Empty message since file is in Storage
        validatedData.hasStoredContent = true // Flag to indicate content is in Storage
        console.log('✅ File metadata prepared for storage (content removed)')
      } else {
        // For regular text subnotes, check size and compress if needed
        const documentSize = getDocumentSizeInBytes(validatedData)
        console.log('📏 Document size:', documentSize, 'bytes (limit:', FIRESTORE_MAX_DOCUMENT_SIZE, ')')

        if (documentSize > FIRESTORE_MAX_DOCUMENT_SIZE) {
          console.log('⚠️ Document too large, attempting content compression...')

          // Try to compress images if it's HTML content
          if (validatedData.message && validatedData.message.includes('<img')) {
            try {
              const compressedMessage = await compressImagesInContent(validatedData.message)
              const compressedSize = getDocumentSizeInBytes({ ...validatedData, message: compressedMessage })
              console.log('🗜️ After compression:', compressedSize, 'bytes')

              if (compressedSize <= FIRESTORE_MAX_DOCUMENT_SIZE) {
                console.log('✅ Compression successful, proceeding with compressed content')
                validatedData.message = compressedMessage
                validatedData.wasCompressed = true
              } else {
                // Still too large after compression
                const sizeMB = (compressedSize / (1024 * 1024)).toFixed(2)
                const limitMB = (FIRESTORE_MAX_DOCUMENT_SIZE / (1024 * 1024)).toFixed(2)
                throw new Error(`Document is too large (${sizeMB}MB) even after compression. Maximum allowed is ${limitMB}MB. Please reduce the file size or remove some images.`)
              }
            } catch (compressionError) {
              // Check if it's our size error or an actual compression failure
              if (compressionError.message.includes('too large')) {
                throw compressionError
              }
              console.error('❌ Image compression failed:', compressionError)
              throw new Error(`Cannot compress document: ${compressionError.message}`)
            }
          } else {
            // No images to compress
            const sizeMB = (documentSize / (1024 * 1024)).toFixed(2)
            const limitMB = (FIRESTORE_MAX_DOCUMENT_SIZE / (1024 * 1024)).toFixed(2)
            throw new Error(`Document is too large (${sizeMB}MB). Maximum allowed is ${limitMB}MB. Please reduce the file size.`)
          }
        }
      }

      console.log('💾 Attempting to store subnote data to Firestore...')
      console.log('📋 Data to store:', {
        ...validatedData,
        message: validatedData.message?.length > 100
          ? validatedData.message.substring(0, 100) + '... (truncated for logging)'
          : validatedData.message
      })

      const docRef = await addDoc(subRef, validatedData)
      const subnoteId = docRef.id

      console.log('✅ Subnote successfully stored in Firestore!', {
        alertId,
        subnoteId,
        fullPath: `alerts/${alertId}/subnotes/${subnoteId}`,
        timestamp: new Date().toISOString()
      })

      // Immediately verify the document was created
      console.log('🔍 Verifying document creation...')
      try {
        const verifyDoc = await getDoc(doc(db, 'alerts', alertId, 'subnotes', subnoteId))
        if (verifyDoc.exists()) {
          console.log('✅ Document verification successful - data exists in Firestore')
          const verifyData = verifyDoc.data()
          console.log('📋 Verified data:', {
            title: verifyData.title,
            messageLength: verifyData.message?.length,
            hasCreatedAt: !!verifyData.createdAt,
            isFileContent: verifyData.isFileContent
          })
        } else {
          console.error('❌ Document verification failed - document not found after creation!')
        }
      } catch (verifyError) {
        console.error('❌ Document verification error:', verifyError.message)
      }

      return subnoteId
    } catch (error) {
      console.error('❌ COMPLETE ERROR in addAlertSubnote:', {
        alertId,
        error: error.message,
        stack: error.stack,
        subnoteData: subnote ? Object.keys(subnote) : 'null',
        timestamp: new Date().toISOString()
      })
      throw new Error(`Failed to create subnote: ${error.message}`)
    }
  }

  const deleteImage = async (imageRefOrPath) => {
    try {
      const imageRef = storageRef(storage, imageRefOrPath)
      await deleteObject(imageRef)
    } catch (error) {
      console.error('Error deleting image:', error)
      throw error
    }
  }

  // ----------------- Firebase Status -----------------
  const isFirebaseReady = () => {
    return !!(db && (auth || true) && storage) // Auth is optional for basic functionality
  }

  // ----------------- Generic Firestore helpers -----------------
  const getDocuments = async (collectionName, filters = null, orderField = 'createdAt') => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        return []
      }
      const colRef = collection(db, collectionName)
      let q
      if (filters && filters.field && typeof filters.value !== 'undefined') {
        q = query(colRef, where(filters.field, '==', filters.value), orderBy(orderField, 'desc'))
      } else {
        q = query(colRef, orderBy(orderField, 'desc'))
      }
      const snapshot = await getDocs(q)
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      console.error(`Error fetching ${collectionName}:`, error)
      return []
    }
  }

  const getDocumentById = async (collectionName, id) => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        return null
      }
      const docRef = doc(db, collectionName, id)
      const snap = await getDoc(docRef)
      return snap.exists() ? { id: snap.id, ...snap.data() } : null
    } catch (error) {
      console.error(`Error fetching ${collectionName} by id:`, error)
      return null
    }
  }

  const addDocument = async (collectionName, data) => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        throw new Error('Firestore not initialized')
      }
      const payload = {
        ...data,
        createdAt: data.createdAt || new Date(),
        createdBy: data.createdBy || 'admin@nursingexam.com'
      }
      const docRef = await addDoc(collection(db, collectionName), payload)
      return docRef.id
    } catch (error) {
      console.error(`Error adding ${collectionName}:`, error)
      throw error
    }
  }

  const updateDocument = async (collectionName, id, data) => {
    try {
      await updateDoc(doc(db, collectionName, id), {
        ...data,
        updatedAt: new Date(),
        updatedBy: data.updatedBy || 'admin@nursingexam.com'
      })
    } catch (error) {
      console.error(`Error updating ${collectionName}:`, error)
      throw error
    }
  }

  const deleteDocument = async (collectionName, id) => {
    try {
      await deleteDoc(doc(db, collectionName, id))
    } catch (error) {
      console.error(`Error deleting ${collectionName}:`, error)
      throw error
    }
  }

  // ----------------- Home Content -----------------
  const getHomeContents = async () => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        return []
      }
      // Get all documents in /home collection that start with "mainCategory_"
      const homeRef = collection(db, 'home')
      const snapshot = await getDocs(homeRef)
      const contents = []

      snapshot.docs.forEach(doc => {
        const data = doc.data()
        // Convert Firestore timestamps to JavaScript Date objects
        const processedData = {
          id: doc.id,
          ...data,
          title: data.CollectionName || data.title || doc.id, // Ensure we have a title for display
          createdAt: convertFirestoreTimestamp(data.createdAt),
          updatedAt: convertFirestoreTimestamp(data.updatedAt)
        }
        contents.push(processedData)
      })

      // Sort by document type and ID
      // Cards (card-N) appear first, sorted by N
      // Then mainCategory documents (mainCategory_N), sorted by N
      contents.sort((a, b) => {
        const isCardA = a.id.startsWith('card-')
        const isCardB = b.id.startsWith('card-')

        // If both are cards or both are mainCategory, sort by number
        if (isCardA === isCardB) {
          const numA = parseInt(a.id.split(/[-_]/)[1]) || 0
          const numB = parseInt(b.id.split(/[-_]/)[1]) || 0
          return numA - numB
        }

        // Cards come before mainCategory
        return isCardA ? -1 : 1
      })

      console.log('📋 Fetched all home contents from /home:', contents.length, contents)
      return contents
    } catch (error) {
      console.error('Error getting home contents:', error)
      throw error
    }
  }

  const addHomeContent = async (data) => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        throw new Error('Firestore not initialized')
      }

      // If imageUrl is a blob URL, upload the image and get the Firestore URL
      let imageUrl = data.imageUrl || ""
      if (imageUrl.startsWith && imageUrl.startsWith('blob:') && data.imageFile) {
        // imageFile should be provided in data for upload
        imageUrl = await uploadImage(data.imageFile, 'home')
      }

      // Automatically set planStatus based on plan field
      const planValue = data.plan || ""
      const planStatus = planValue && planValue.trim() !== '' && planValue.toLowerCase() !== 'free'
        ? 'paid'
        : 'free'

      // Ensure proper data structure exactly as you specified
      const homeContentData = {
        title: data.title || "",
        imageUrl: imageUrl, // will be Firestore URL if uploaded, otherwise as provided
        testCounts: Number(data.testCounts || data.testsCount || 0),
        plan: planValue, // Add plan field
        planStatus, // Automatically set based on plan
        discountPlan: data.discountPlan || "", // New discount plan field
        index: data.index || undefined, // Add index field
        initialized: true, // Add initialized field as shown in your structure
        createdAt: new Date(), // This creates a Firestore Timestamp
      }

      console.log('💾 Adding home content with structure:', homeContentData)
      console.log('💾 testCounts value:', homeContentData.testCounts, 'type:', typeof homeContentData.testCounts)

      // Find the next available mainCategory number
      const homeRef = collection(db, 'home')
      const snapshot = await getDocs(homeRef)
      let maxNumber = 0

      snapshot.docs.forEach(doc => {
        if (doc.id.startsWith('mainCategory_')) {
          const number = parseInt(doc.id.split('_')[1]) || 0
          if (number > maxNumber) {
            maxNumber = number
          }
        }
      })

      const nextNumber = maxNumber + 1
      const docName = `mainCategory_${nextNumber}`

      // Create document with the numbered name
      const docRef = doc(db, 'home', docName)
      await setDoc(docRef, homeContentData)

      // Update metadata if index is provided
      if (data.index) {
        const metaRef = doc(db, 'meta', 'homeContent')
        const metaSnap = await getDoc(metaRef)
        const currentLastIndex = metaSnap.exists() ? (metaSnap.data().lastIndex || 0) : 0
        const newIndex = parseInt(data.index)

        if (newIndex > currentLastIndex) {
          await setDoc(metaRef, {
            lastIndex: newIndex,
            lastUpdated: new Date()
          }, { merge: true })
        }
      }

      console.log('✅ Home content added at path: /home/' + docName)
      console.log('✅ Document data saved:', homeContentData)
      return docName
    } catch (error) {
      console.error('Error adding home content:', error)
      throw error
    }
  }

  const updateHomeContent = async (id, data) => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        throw new Error('Firestore not initialized')
      }

      // Update any document in /home collection (card- or mainCategory_)
      const docRef = doc(db, 'home', id)

      // Automatically set planStatus based on plan field
      const planValue = data.plan !== undefined ? data.plan : ""
      const planStatus = planValue && planValue.trim() !== '' && planValue.toLowerCase() !== 'free'
        ? 'paid'
        : 'free'

      // Ensure proper data structure for both card- and mainCategory_ documents
      const updateData = {
        ...data,
        // Set both title and CollectionName for compatibility with both document types
        title: data.title || data.CollectionName || "",
        CollectionName: data.title || data.CollectionName || "",
        testCounts: Number(data.testCounts || data.testsCount || 0),
        planStatus, // Automatically set based on plan
        initialized: true, // Maintain initialized field
        updatedAt: new Date()
      }

      console.log('💾 Updating home content (id:', id, ') with testCounts:', updateData.testCounts, 'type:', typeof updateData.testCounts)

      await updateDoc(docRef, updateData)
      console.log('✅ Home content updated at path: /home/' + id)
      console.log('✅ Updated data:', updateData)

      // Propagate changes to any subscription cards that were sourced from this home content
      await syncSubscriptionCardsFromHome(id, updateData)
    } catch (error) {
      console.error('Error updating home content:', error)
      throw error
    }
  }

  const deleteHomeContent = async (id) => {
    try {
      // Delete specific mainCategory document
      const docRef = doc(db, 'home', id)
      await deleteDoc(docRef)
      console.log('🗑️ Home content deleted at path: /home/' + id)

      // Remove any subscription cards that reference this home content
      await removeHomeContentFromSubscriptions(id)
    } catch (error) {
      console.error('Error deleting home content:', error)
      throw error
    }
  }

  // Deletes a home content and all its nested tests and questions from Firestore
  const deleteHomeContentCascade = async (id) => {
    try {
      console.log('🔥 Cascade deleting home content with nested tests/questions:', id)
      const dynamicDocRef = doc(db, 'home', id)
      const testManageRef = collection(dynamicDocRef, 'testManage')
      const testsSnap = await getDocs(testManageRef)

      // Collect all document refs to delete (questions, tests, then parent)
      const refsToDelete = []

      // Fetch all question sets in parallel for speed
      const questionSnapResults = await Promise.all(
        testsSnap.docs.map(async (testDoc) => {
          const questionSetRef = collection(testDoc.ref, 'questionSet')
          const questionsSnap = await getDocs(questionSetRef)
          return { testRef: testDoc.ref, questionRefs: questionsSnap.docs.map(d => d.ref) }
        })
      )

      questionSnapResults.forEach(({ testRef, questionRefs }) => {
        questionRefs.forEach(ref => refsToDelete.push(ref))
        refsToDelete.push(testRef)
      })

      // Finally, delete the home content document itself
      refsToDelete.push(dynamicDocRef)

      // Commit deletes in chunks (Firestore limit ~500 ops per batch)
      const chunkSize = 450
      for (let i = 0; i < refsToDelete.length; i += chunkSize) {
        const batch = writeBatch(db)
        const slice = refsToDelete.slice(i, i + chunkSize)
        slice.forEach(ref => batch.delete(ref))
        await batch.commit()
        console.log(`✅ Committed delete batch ${Math.floor(i / chunkSize) + 1} with ${slice.length} ops`)
      }

      console.log('✅ Cascade deleted home content at /home/' + id)

      // Clean up matching subscription cards after cascade delete
      await removeHomeContentFromSubscriptions(id)
      return true
    } catch (error) {
      console.error('❌ Error cascade deleting home content:', error)
      throw error
    }
  }

  // Keep subscription cards in sync when a home content item is edited
  const syncSubscriptionCardsFromHome = async (homeContentId, homeData = {}) => {
    const subscriptionsSnap = await getDocs(collection(db, 'Subscription'))
    const normalizedTestCount = Number(homeData.testCounts ?? homeData.testsCount ?? 0)
    let updatedSubscriptions = 0

    for (const subDoc of subscriptionsSnap.docs) {
      const data = subDoc.data() || {}
      const cards = data.cards || []
      let hasMatch = false

      const updatedCards = cards.map(card => {
        if (card.homeContentId === homeContentId) {
          hasMatch = true
          return {
            ...card,
            title: homeData.title ?? card.title,
            collectionName: homeData.title ?? card.collectionName,
            TestCount: normalizedTestCount ? normalizedTestCount.toString() : (card.TestCount || card.testCount || '0'),
            imageUrl: homeData.imageUrl ?? card.imageUrl,
            updatedFromHomeAt: new Date()
          }
        }
        return card
      })

      if (hasMatch) {
        await updateDoc(subDoc.ref, {
          cards: updatedCards,
          cardsCount: updatedCards.length
        })

        // Also update the card documents inside the subscription's cards subcollection
        const cardsCollectionRef = collection(subDoc.ref, 'cards')
        const cardDocs = await getDocs(cardsCollectionRef)

        for (const cardDoc of cardDocs.docs) {
          const cardData = cardDoc.data() || {}
          if (cardData.copiedFrom === homeContentId) {
            await setDoc(cardDoc.ref, {
              ...cardData,
              title: homeData.title ?? cardData.title,
              imageUrl: homeData.imageUrl ?? cardData.imageUrl,
              testCounts: normalizedTestCount || cardData.testCounts || 0,
              updatedFromHomeAt: new Date()
            }, { merge: true })
          }
        }

        updatedSubscriptions++
      }
    }

    console.log(`🔄 Synced home content ${homeContentId} to ${updatedSubscriptions} subscription(s)`)
    return updatedSubscriptions
  }

  // Remove subscription cards that pointed at a deleted home content item
  const removeHomeContentFromSubscriptions = async (homeContentId) => {
    const subscriptionsSnap = await getDocs(collection(db, 'Subscription'))
    let cleanedSubscriptions = 0

    for (const subDoc of subscriptionsSnap.docs) {
      const data = subDoc.data() || {}
      const cards = data.cards || []
      const filteredCards = cards.filter(card => card.homeContentId !== homeContentId)

      const hadReferences = filteredCards.length !== cards.length
      if (hadReferences) {
        await updateDoc(subDoc.ref, {
          cards: filteredCards,
          cardsCount: filteredCards.length
        })

        // Remove matching card documents from the cards subcollection
        const cardsCollectionRef = collection(subDoc.ref, 'cards')
        const cardDocs = await getDocs(cardsCollectionRef)
        const deletes = cardDocs.docs
          .filter(docSnap => (docSnap.data() || {}).copiedFrom === homeContentId)
          .map(docSnap => deleteDoc(docSnap.ref))
        await Promise.all(deletes)

        cleanedSubscriptions++
      }
    }

    console.log(`🧹 Removed home content ${homeContentId} from ${cleanedSubscriptions} subscription(s)`)
    return cleanedSubscriptions
  }

  // Helper function to sync testCounts with actual test count
  const syncTestCounts = async (contentId) => {
    try {
      console.log('🔄 Syncing testCounts for contentId:', contentId)

      const dynamicDocRef = doc(db, 'home', contentId)
      const testManageRef = collection(dynamicDocRef, 'testManage')
      const snapshot = await getCountFromServer(testManageRef)

      const actualTestCount = snapshot.data().count

      // Use setDoc with merge to ensure the field is created/updated even if document doesn't exist
      await setDoc(dynamicDocRef, {
        testCounts: actualTestCount,
        initialized: true
      }, { merge: true })

      console.log('✅ Synced testCounts to:', actualTestCount, 'for path: /home/' + contentId)
      return actualTestCount
    } catch (error) {
      console.error('Error syncing testCounts:', error)
      throw error
    }
  }

  // Get home content metadata
  const getHomeContentMeta = async () => {
    try {
      const metaRef = doc(db, 'meta', 'homeContent')
      const snapshot = await getDoc(metaRef)
      return snapshot.exists() ? snapshot.data() : null
    } catch (error) {
      console.error('Error getting home content meta:', error)
      throw error
    }
  }

  // Get home content by ID
  const getHomeContentById = async (id) => {
    try {
      const docRef = doc(db, 'home', id)
      const snapshot = await getDoc(docRef)
      if (snapshot.exists()) {
        return { id: snapshot.id, ...snapshot.data() }
      }
      return null
    } catch (error) {
      console.error('Error getting home content by ID:', error)
      throw error
    }
  }

  // Bulk sync home content indices
  const bulkSyncHomeContentIndices = async () => {
    try {
      const contents = await getHomeContents()

      // Sort by createdAt to maintain chronological order
      const sortedContents = contents.sort((a, b) => {
        const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0)
        const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0)
        return timeA - timeB
      })

      // Assign sequential indices
      const batch = writeBatch(db)
      sortedContents.forEach((content, index) => {
        const contentRef = doc(db, 'home', content.id)
        batch.update(contentRef, { index: index + 1 })
      })

      await batch.commit()

      // Update metadata
      const metaRef = doc(db, 'meta', 'homeContent')
      await setDoc(metaRef, {
        lastIndex: sortedContents.length,
        lastUpdated: new Date()
      }, { merge: true })

      console.log(`✅ Synced ${sortedContents.length} home content indices`)
      return sortedContents.length
    } catch (error) {
      console.error('Error syncing home content indices:', error)
      throw error
    }
  }

  // Create a card for home content
  const createHomeContentCard = async (homeContentId, cardData) => {
    try {
      console.log(`Creating card for home content ${homeContentId}...`)

      // 1. Generate new card ID (card-N)
      const homeRef = collection(db, 'home')
      const snapshot = await getDocs(homeRef)
      let maxId = 0
      snapshot.docs.forEach(doc => {
        if (isCardId(doc.id)) {
          const numPart = parseInt(doc.id.split('-')[1])
          if (!isNaN(numPart) && numPart > maxId) maxId = numPart
        }
      })
      const newCardId = `card-${maxId + 1}`
      const newCardRef = doc(db, 'home', newCardId)

      // 2. Prepare card data
      let contentToSave = {
        id: newCardId,
        CollectionName: cardData.title,
        title: cardData.title,
        TestCount: cardData.TestCount || '0',
        imageUrl: cardData.imageUrl || '',
        discountPlan: cardData.discountPlan || '',
        createdAt: new Date(),
        type: 'home_content_card',
        linkedHomeContentId: homeContentId
      }

      if (cardData.homeContentId) {
        const sourceRef = doc(db, 'home', cardData.homeContentId)
        const sourceSnap = await getDoc(sourceRef)
        if (sourceSnap.exists()) {
          const sourceData = sourceSnap.data()
          const { plan, planStatus, ...safeSourceData } = sourceData
          contentToSave = {
            ...contentToSave,
            ...safeSourceData,
            CollectionName: cardData.title,
            title: cardData.title,
            imageUrl: cardData.imageUrl || sourceData.imageUrl || ''
          }
        }
      }

      // 3. Save to home/card-N
      await setDoc(newCardRef, contentToSave)
      console.log(`✅ Created card document: home/${newCardId}`)

      // 4. Copy subcollections if source exists
      if (cardData.homeContentId) {
        await copySubCollections(cardData.homeContentId, newCardId, cardData.sourceCollection || 'home')
      }

      // 5. Link in home content document
      const homeContentRef = doc(db, 'home', homeContentId)
      await updateDoc(homeContentRef, {
        cardIds: arrayUnion(newCardId),
        cardsCount: (cardData.currentCardCount || 0) + 1
      })

      return newCardId
    } catch (error) {
      console.error('Error creating home content card:', error)
      throw error
    }
  }

  // Update home content card
  const updateHomeContentCard = async (cardId, updates) => {
    try {
      const cardRef = doc(db, 'home', cardId)
      await updateDoc(cardRef, {
        ...updates,
        updatedAt: new Date()
      })
      console.log(`✅ Updated card: ${cardId}`)
    } catch (error) {
      console.error('Error updating home content card:', error)
      throw error
    }
  }

  // Remove home content card
  const removeHomeContentCard = async (homeContentId, cardId) => {
    try {
      console.log(`🗑️ Removing card ${cardId} from home content ${homeContentId}`)

      // 1. Get home content doc to check existing cards
      const homeContentRef = doc(db, 'home', homeContentId)
      const homeContentSnap = await getDoc(homeContentRef)

      if (homeContentSnap.exists()) {
        const data = homeContentSnap.data()
        const cardIds = data.cardIds || []

        // 2. Remove from cardIds array
        const updatedCardIds = cardIds.filter(id => id !== cardId)
        await updateDoc(homeContentRef, {
          cardIds: updatedCardIds,
          cardsCount: updatedCardIds.length
        })
      }

      // 3. Delete card document
      const cardRef = doc(db, 'home', cardId)
      const cardSnap = await getDoc(cardRef)
      if (cardSnap.exists()) {
        const cardData = cardSnap.data()
        if (cardData.imageUrl && cardData.imageUrl.includes('firebase')) {
          try {
            await deleteImage(cardData.imageUrl)
          } catch (err) {
            console.warn('Failed to delete card image:', err)
          }
        }
      }

      // 4. Delete card document and its subcollections
      await deleteDoc(cardRef)

      // Delete testManage subcollection
      const testManageRef = collection(cardRef, 'testManage')
      const testManageSnap = await getDocs(testManageRef)
      for (const testDoc of testManageSnap.docs) {
        // Delete questionSet for each test
        const questionSetRef = collection(testDoc.ref, 'questionSet')
        const questionSetSnap = await getDocs(questionSetRef)
        for (const questionDoc of questionSetSnap.docs) {
          await deleteDoc(questionDoc.ref)
        }
        await deleteDoc(testDoc.ref)
      }

      console.log(`✅ Removed card ${cardId} from home content`)
    } catch (error) {
      console.error('Error removing home content card:', error)
      throw error
    }
  }

  // Add card directly to /home without creating a package
  const addCardDirectly = async (cardData, subscriptionId = null) => {
    try {
      console.log('Adding card directly to /home...' + (subscriptionId ? ` and linking to ${subscriptionId}` : ''))

      // 1. Generate new card ID (card-N)
      const homeRef = collection(db, 'home')
      const snapshot = await getDocs(homeRef)
      let maxId = 0
      snapshot.docs.forEach(doc => {
        if (isCardId(doc.id)) {
          const numPart = parseInt(doc.id.split('-')[1])
          if (!isNaN(numPart) && numPart > maxId) maxId = numPart
        }
      })
      const newCardId = `card-${maxId + 1}`
      const newCardRef = doc(db, 'home', newCardId)

      // 2. Prepare card data
      const cardToSave = {
        id: newCardId,
        CollectionName: cardData.title,
        title: cardData.title,
        imageUrl: cardData.imageUrl || '',
        testCounts: Number(cardData.testCounts || 0),
        plan: cardData.plan || null,
        planStatus: (cardData.plan && cardData.plan !== 'Free') ? 'paid' : 'free',
        discountPlan: cardData.discountPlan || '',
        index: cardData.index || undefined,
        initialized: true,
        createdAt: new Date(),
        type: 'standalone_card' // Mark as standalone (not linked to any package)
      }

      // 3. Save to home/card-N
      await setDoc(newCardRef, cardToSave)
      console.log(`✅ Created standalone card: home/${newCardId}`)

      // 4. Update metadata if index is provided
      if (cardData.index) {
        const metaRef = doc(db, 'meta', 'homeContent')
        const metaSnap = await getDoc(metaRef)
        const currentLastIndex = metaSnap.exists() ? (metaSnap.data().lastIndex || 0) : 0
        const newIndex = parseInt(cardData.index)

        if (newIndex > currentLastIndex) {
          await setDoc(metaRef, {
            lastIndex: newIndex,
            lastUpdated: new Date()
          }, { merge: true })
        }
      }

      // 5. Link to Subscription if ID provided
      if (subscriptionId) {
        const subRef = doc(db, 'Subscription', subscriptionId)
        await updateDoc(subRef, {
          cardIds: arrayUnion(newCardId),
          cardsCount: increment(1)
        })
        console.log(`✅ Linked card ${newCardId} to Subscription ${subscriptionId}`)
      }

      return newCardId
    } catch (error) {
      console.error('Error adding card directly:', error)
      throw error
    }
  }

  // Update card directly
  const updateCardDirectly = async (cardId, cardData) => {
    try {
      const cardRef = doc(db, 'home', cardId)

      // Ensure both title and CollectionName are set for compatibility
      const updateData = {
        ...cardData,
        title: cardData.title || cardData.CollectionName || "",
        CollectionName: cardData.title || cardData.CollectionName || "",
        testCounts: Number(cardData.testCounts || 0),
        planStatus: (cardData.plan && cardData.plan !== 'Free') ? 'paid' : 'free',
        initialized: true,
        updatedAt: new Date()
      }

      await updateDoc(cardRef, updateData)
      console.log(`✅ Updated card: ${cardId}`)
      return cardId
    } catch (error) {
      console.error('Error updating card directly:', error)
      throw error
    }
  }

  // ----------------- Nursing Bullets -----------------
  const getNursingBullets = async () => getDocuments('nursingBullets')
  const addNursingBullet = async (bulletData) => addDocument('nursingBullets', bulletData)
  const updateNursingBullet = async (id, bulletData) => updateDocument('nursingBullets', id, bulletData)
  const deleteBullet = async (id) => deleteDocument('nursingBullets', id)

  // ----------------- Questions -----------------
  const getQuestions = async (filters = {}) => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        return []
      }
      let qRef = collection(db, 'questions')
      if (filters.category) qRef = query(qRef, where('category', '==', filters.category))
      qRef = query(qRef, orderBy('createdAt', 'desc'))
      const snap = await getDocs(qRef)
      let questions = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      if (filters.search) {
        const term = filters.search.toLowerCase()
        questions = questions.filter(q => (q.question || '').toLowerCase().includes(term) || (q.explanation || '').toLowerCase().includes(term))
      }
      return questions
    } catch (error) {
      console.error('Error fetching questions:', error)
      return []
    }
  }

  const getQuestion = async (id) => getDocumentById('questions', id)
  const addQuestion = async (data) => addDocument('questions', data)

  const updateQuestion = async (id, data) => {
    try {
      // Get the old question data to check for images to delete
      const oldQuestion = await getQuestion(id)

      if (oldQuestion) {
        // Check if image URLs are being replaced
        const oldImageUrls = []
        const newImageUrls = []

        if (oldQuestion.imageUrl) oldImageUrls.push(oldQuestion.imageUrl)
        if (oldQuestion.questionImage) oldImageUrls.push(oldQuestion.questionImage)
        if (data.imageUrl) newImageUrls.push(data.imageUrl)
        if (data.questionImage) newImageUrls.push(data.questionImage)

        // Delete old images if they're being replaced with new ones
        for (const oldUrl of oldImageUrls) {
          if (oldUrl && !newImageUrls.includes(oldUrl) && oldUrl.includes('firebase')) {
            try {
              await deleteImage(oldUrl)
              console.log('🗑️ Deleted old question image from storage:', oldUrl)
            } catch (err) {
              console.warn('⚠️ Failed to delete old image:', err.message)
            }
          }
        }
      }

      return await updateDocument('questions', id, data)
    } catch (error) {
      console.error('Error updating question:', error)
      throw error
    }
  }

  const deleteQuestion = async (id) => {
    try {
      // Get question data to check for storage files
      const question = await getQuestion(id)

      if (question) {
        // Collect all storage URLs
        const storageUrls = []
        if (question.imageUrl) storageUrls.push(question.imageUrl)
        if (question.questionImage) storageUrls.push(question.questionImage)
        if (question.options) {
          question.options.forEach(opt => {
            if (typeof opt === 'object' && opt.imageUrl) storageUrls.push(opt.imageUrl)
          })
        }

        // Delete files from storage
        for (const url of storageUrls) {
          if (url && url.includes('firebase')) {
            try {
              await deleteImage(url)
              console.log('🗑️ Deleted question image from storage:', url)
            } catch (err) {
              console.warn('⚠️ Failed to delete storage file:', err.message)
            }
          }
        }
      }

      return await deleteDocument('questions', id)
    } catch (error) {
      console.error('Error deleting question:', error)
      throw error
    }
  }

  // ----------------- Categories -----------------
  const getCategories = async () => getDocuments('categories', null, 'name')
  const getCategory = async (id) => getDocumentById('categories', id)
  const addCategory = async (data) => addDocument('categories', data)
  const updateCategory = async (id, data) => updateDocument('categories', id, data)
  const deleteCategory = async (id) => deleteDocument('categories', id)

  // ----------------- Users -----------------
  const getUsers = async () => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        return []
      }
      const colRef = collection(db, 'users')
      // Fetch all users without ordering to ensure we get documents even if they miss specific fields
      const snapshot = await getDocs(colRef)
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      console.error('Error fetching users:', error)
      return []
    }
  }
  const getUser = async (id) => getDocumentById('users', id)

  // ----------------- Reports -----------------
  const getReports = async () => {
    try {
      const reportsRef = collection(db, 'report')
      const reportsSnapshot = await getDocs(reportsRef)
      const reports = []

      reportsSnapshot.forEach((doc) => {
        const data = doc.data()
        reports.push({
          id: doc.id,
          userName: data.userName || 'N/A',
          email: data.email || 'N/A',
          report: data.report || '',
          submitedDate: data.submitedDate || null
        })
      })

      return reports
    } catch (error) {
      console.error('Error getting reports:', error)
      throw error
    }
  }

  const deleteReport = async (reportId) => {
    try {
      const reportRef = doc(db, 'report', reportId)
      await deleteDoc(reportRef)
    } catch (error) {
      console.error('Error deleting report:', error)
      throw error
    }
  }
  const createUser = async (userData) => {
    try {
      // Use email as document ID
      const userDocRef = doc(db, 'users', userData.email)
      await setDoc(userDocRef, {
        ...userData,
        createdAt: new Date(),
        updatedAt: new Date()
      })

      // Also create leaderboard entry for new user
      await createLeaderboardEntry(userData)

      return userData.email
    } catch (error) {
      console.error('Error creating user:', error)
      throw error
    }
  }

  // Helper function to create leaderboard entry for new user
  const createLeaderboardEntry = async (userData) => {
    try {
      if (!userData.email) {
        console.warn('Cannot create leaderboard entry: email is required')
        return
      }

      // Get current number of users to assign next rank
      const existingLeaderboardData = await getCountFromServer(collection(db, 'leaderboard'))
      const nextRank = existingLeaderboardData.data().count + 1

      const leaderboardData = {
        email: userData.email,
        name: userData.name || "",
        offlineTestMarks: 0,
        onlineTestMarks: 0,
        profileImg: userData.profileImage || userData.profileImg || "",
        rank: nextRank,
        submitedDate: new Date(), // Set current date as initial submission date
        totalMarks: 0,
        weeklyMarks: 0,
        monthlyMarks: 0
      }

      // Use email as document ID
      const leaderboardDocRef = doc(db, 'leaderboard', userData.email)
      await setDoc(leaderboardDocRef, leaderboardData)

      console.log(`✅ Created leaderboard entry for ${userData.name} at /leaderboard/${userData.email}`)
    } catch (error) {
      console.error('Error creating leaderboard entry:', error)
      // Don't throw error here so user creation still succeeds even if leaderboard creation fails
    }
  }

  const updateUser = async (id, data) => {
    try {
      // Check for old profile image to delete from Storage if being replaced
      if (data.profileImg !== undefined || data.profileImage !== undefined) {
        try {
          const userDoc = await getDoc(doc(db, 'users', id))
          if (userDoc.exists()) {
            const currentData = userDoc.data()
            const oldProfileImg = currentData.profileImg || currentData.profileImage
            const newProfileImg = data.profileImg || data.profileImage

            // Delete old profile image if it exists and is different from new one
            if (oldProfileImg && oldProfileImg !== newProfileImg && oldProfileImg.includes('firebase')) {
              console.log('🗑️ Deleting old user profile image from Storage:', oldProfileImg)
              try {
                await deleteImage(oldProfileImg)
                console.log('✅ Old user profile image deleted from Storage')
              } catch (deleteError) {
                console.warn('⚠️ Could not delete old user profile image (may not exist):', deleteError.message)
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ Could not check for old user profile image:', error.message)
        }
      }

      await updateDocument('users', id, data)
    } catch (error) {
      console.error('Error updating user:', error)
      throw error
    }
  }

  const deleteUser = async (id) => {
    try {
      console.log('🗑️ Deleting user from Firestore:', id)

      // Get the user document to check for profile image
      const userDoc = await getDoc(doc(db, 'users', id))
      if (userDoc.exists()) {
        const data = userDoc.data()
        const profileImg = data.profileImg || data.profileImage

        // Delete profile image from Storage if it exists
        if (profileImg && profileImg.includes('firebase')) {
          console.log('🗑️ Deleting user profile image from Storage:', profileImg)
          try {
            await deleteImage(profileImg)
            console.log('✅ User profile image deleted from Storage')
          } catch (deleteError) {
            console.warn('⚠️ Could not delete user profile image (may not exist):', deleteError.message)
            // Continue with user deletion even if image deletion fails
          }
        }

        // Also delete from leaderboard if exists
        try {
          const leaderboardDoc = doc(db, 'leaderboard', id)
          const leaderboardSnap = await getDoc(leaderboardDoc)
          if (leaderboardSnap.exists()) {
            await deleteDoc(leaderboardDoc)
            console.log('✅ User leaderboard entry deleted')
          }
        } catch (leaderboardError) {
          console.warn('⚠️ Could not delete leaderboard entry:', leaderboardError.message)
        }
      }

      // Delete the user document
      await deleteDocument('users', id)
      console.log('✅ User deleted from Firestore')
    } catch (error) {
      console.error('Error deleting user:', error)
      throw error
    }
  }

  // Fetch all user purchases across all users and root collections
  const getAllUserPurchases = async () => {
    try {
      console.log('Fetching all user purchases from Firestore...')
      const allPurchases = []
      const processedIds = new Set()

      // Helper to add purchase and avoid duplicates
      const addPurchase = (doc) => {
        if (!processedIds.has(doc.id)) {
          processedIds.add(doc.id)
          const data = doc.data()
          allPurchases.push({
            id: doc.id,
            userEmail: data.userEmail || 'N/A',
            ...data,
            timestamp: convertFirestoreTimestamp(data.timestamp)
          })
        }
      }

      // 1. Fetch from collection groups (this finds 'purchases' subcollections anywhere)
      try {
        const purchasesGroupSnap = await getDocs(collectionGroup(db, 'purchases'))
        purchasesGroupSnap.forEach(doc => addPurchase(doc))
      } catch (e) {
        console.log('No collection group purchases or error:', e)
      }

      try {
        const purchesGroupSnap = await getDocs(collectionGroup(db, 'purches'))
        purchesGroupSnap.forEach(doc => addPurchase(doc))
      } catch (e) {
        console.log('No collection group purches or error:', e)
      }

      // 2. Fetch from root collections just in case
      try {
        const rootPurchasesSnap = await getDocs(collection(db, 'purchases'))
        rootPurchasesSnap.forEach(doc => addPurchase(doc))
      } catch (e) {}

      try {
        const rootPurchesSnap = await getDocs(collection(db, 'purches'))
        rootPurchesSnap.forEach(doc => addPurchase(doc))
      } catch (e) {}

      console.log(`Fetched total ${allPurchases.length} purchases from all possible locations`)
      return allPurchases
    } catch (error) {
      console.error('Error fetching all user purchases:', error)
      return []
    }
  }

  // ----------------- Results -----------------
  const getResults = async (filters = {}) => {
    try {
      let qRef = collection(db, 'results')
      if (filters.userId) qRef = query(qRef, where('userId', '==', filters.userId))
      qRef = query(qRef, orderBy('createdAt', 'desc'))
      const snap = await getDocs(qRef)
      return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      console.error('Error fetching results:', error)
      return []
    }
  }

  // ----------------- Test Questions (cached) -----------------
  const getTestQuestions = async (contentId, forceRefresh = false) => {
    if (!contentId) return []

    // Use cache unless force refresh is requested
    if (!forceRefresh && testCache.has(contentId)) {
      console.log('📋 Using cached tests for contentId:', contentId)
      return testCache.get(contentId)
    }

    try {
      console.log('🔄 Fetching fresh tests from /home/' + contentId + '/testManage (dynamicData=' + contentId + '):', contentId)

      // Read from /home/{contentId}/testManage/ path (dynamic path)
      const dynamicDocRef = doc(db, 'home', contentId)
      const testManageRef = collection(dynamicDocRef, 'testManage')
      const snap = await getDocs(testManageRef)

      const tests = []

      // For each test, also fetch its questions from questionSet subcollection
      for (const testDoc of snap.docs) {
        const testData = testDoc.data()
        const testId = testDoc.id

        try {
          // Fetch questions from /home/{contentId}/testManage/{testId}/questionSet/
          const testDocRef = doc(testManageRef, testId)
          const questionSetRef = collection(testDocRef, 'questionSet')
          const questionsSnap = await getDocs(questionSetRef)

          const questions = []
          questionsSnap.docs.forEach(qDoc => {
            const qData = qDoc.data()

            // Transform from your Firestore structure back to the format expected by the app
            let correctAnswer = 0 // default to option A
            if (qData.options) {
              if (qData.options.optionA) correctAnswer = 0
              else if (qData.options.optionB) correctAnswer = 1
              else if (qData.options.optionC) correctAnswer = 2
              else if (qData.options.optionD) correctAnswer = 3
            }

            // Get option texts from optionTexts field, fallback to generic labels
            const optionTexts = qData.optionTexts || {}
            const options = [
              optionTexts.optionA || 'Option A',
              optionTexts.optionB || 'Option B',
              optionTexts.optionC || 'Option C',
              optionTexts.optionD || 'Option D'
            ]

            questions.push({
              id: qDoc.id,
              question: qData.question || "",
              explanation: qData.explanation || "",
              options: options,
              correctAnswer: correctAnswer,
              questionType: qData.questionType || 'text',
              questionImageURL: qData.questionImageURL || null
            })

            // DEBUG: Log loaded image URLs
            if (qData.questionImageURL) {
              console.log(`  ✅ Question ${qDoc.id} has imageURL: ${qData.questionImageURL.substring(0, 50)}...`)
            }
          })

          // Sort questions by document name ({contentId}_question_1, {contentId}_question_2, etc.)
          questions.sort((a, b) => {
            // Extract question number from document name pattern: {contentId}_question_{N}
            const numA = parseInt(a.id.split('_question_')[1]) || 0
            const numB = parseInt(b.id.split('_question_')[1]) || 0
            return numA - numB
          })

          tests.push({
            id: testId,
            ...testData,
            questions: questions,
            totalQuestions: questions.length,
            createdAt: convertFirestoreTimestamp(testData.createdAt) ||
              convertFirestoreTimestamp(testData.startDate) ||
              new Date(),
            startDate: convertFirestoreTimestamp(testData.startDate),
            endDate: convertFirestoreTimestamp(testData.endDate)
          })

          console.log(`📋 Fetched ${questions.length} questions for test ${testId}`)
        } catch (questionError) {
          console.error(`Error fetching questions for test ${testId}:`, questionError)
          // Add test without questions if question fetch fails
          tests.push({
            id: testId,
            ...testData,
            questions: [],
            totalQuestions: 0,
            createdAt: convertFirestoreTimestamp(testData.createdAt) ||
              convertFirestoreTimestamp(testData.startDate) ||
              new Date(),
            startDate: convertFirestoreTimestamp(testData.startDate),
            endDate: convertFirestoreTimestamp(testData.endDate)
          })
        }
      }

      // Sort tests by ID (test1, test2, ...) ascending for stable sequential order
      tests.sort((a, b) => {
        const numA = parseInt(a.id.replace('test', '')) || 0
        const numB = parseInt(b.id.replace('test', '')) || 0
        return numA - numB
      })

      console.log('📋 Fetched tests from /home/' + contentId + '/testManage (dynamicData=' + contentId + '):', tests)
      setTestCache(prev => new Map(prev).set(contentId, tests))
      return tests
    } catch (error) {
      console.error('Error fetching test questions from /home/' + contentId + '/testManage:', error)
      return []
    }
  }

  // Clear cache for specific contentId (useful after deletions)
  const clearTestCache = (contentId) => {
    setTestCache(prev => {
      const newCache = new Map(prev)
      newCache.delete(contentId)
      console.log('🧹 Cleared test cache for contentId:', contentId)
      return newCache
    })
  }

  const addTestQuestions = async (contentId, questionsData) => {
    try {
      console.log('🔥 Adding test questions to path /home/' + contentId + '/testManage (dynamicData=' + contentId + '):', questionsData)

      // Check if test already exists by name instead of creating a new one
      const dynamicDocRef = doc(db, 'home', contentId)
      const testManageRef = collection(dynamicDocRef, 'testManage')
      const snapshot = await getDocs(testManageRef)

      let testDocName = null
      let existingTest = null

      // Look for existing test with the same name
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        if (data.testName === questionsData.testName) {
          testDocName = doc.id
          existingTest = data
          console.log('📋 Found existing test:', testDocName, data)
        }
      })

      // If no existing test found, create new one
      if (!testDocName) {
        testDocName = await createTestMetadata(contentId, {
          testName: questionsData.testName || `Test for ${contentId}`,
          startDate: questionsData.startDate || new Date(),
          endDate: questionsData.endDate || new Date(),
          duration: questionsData.duration || 60,
          questionsCount: questionsData.questions?.length || 0,
          marks: Number(questionsData.marks) || 0
        })
        console.log('📋 Created new test:', testDocName)
        // Note: testCounts is already synced in createTestMetadata
      } else {
        // Update existing test with question count and preserve marks
        const updateTestDocRef = doc(testManageRef, testDocName)
        await updateDoc(updateTestDocRef, {
          quetionsCount: questionsData.questions?.length || 0,
          marks: Number(existingTest?.marks || questionsData.marks) || 0,
          updatedAt: new Date()
        })
        console.log('📋 Updated existing test question count and marks:', testDocName)
      }

      // Now add questions to the questionSet subcollection
      const testDocRef = doc(testManageRef, testDocName)
      const questionSetRef = collection(testDocRef, 'questionSet')

      // Calculate marks per question (equal distribution)
      // Use marks from existing test if available, otherwise from questionsData
      const totalMarks = parseInt(existingTest?.marks || questionsData.marks) || 0
      const totalQuestions = questionsData.questions.length
      const marksPerQuestion = totalQuestions > 0 ? Math.round(totalMarks / totalQuestions) : 0

      console.log(`📊 Distributing ${totalMarks} marks equally among ${totalQuestions} questions = ${marksPerQuestion} marks per question`)

      // Process and save each question with your specified structure
      for (let i = 0; i < questionsData.questions.length; i++) {
        const questionData = questionsData.questions[i]
        const questionDocName = `${contentId}_question_${i + 1}` // Use dynamic data (contentId) in question document name

        // DEBUG: Log image handling
        console.log(`🐛 DEBUG addTestQuestions - Q${i + 1}:`, {
          questionImagePreview: questionData.questionImagePreview,
          questionImage: questionData.questionImage,
          questionType: questionData.questionType
        })

        // Upload question image if provided, otherwise preserve existing image URL
        let questionImageURL = null
        if (questionData.questionImage) {
          // New image provided, upload it
          questionImageURL = await uploadQuestionImage(questionData.questionImage, contentId, questionDocName)
        } else if (questionData.questionImagePreview && typeof questionData.questionImagePreview === 'string' && questionData.questionImagePreview.length > 0) {
          // No new image, but existing image URL provided, preserve it
          questionImageURL = questionData.questionImagePreview
          console.log(`  ✅ Preserving image URL: ${questionImageURL}`)
        } else {
          console.log(`  ⚠️  No image for Q${i + 1}: preview="${questionData.questionImagePreview}" (type=${typeof questionData.questionImagePreview})`)
        }

        // Transform question data to match your Firestore structure
        const questionPayload = {
          question: questionData.question || "",
          explanation: questionData.explanation || "",
          marks: marksPerQuestion, // Add marks per question
          questionType: questionData.questionType || 'text', // 'text' or 'image'
          questionImageURL: questionImageURL || null, // Image URL if image question
          options: {
            optionA: questionData.correctAnswer === 0, // boolean: true if this is correct answer
            optionB: questionData.correctAnswer === 1, // boolean: true if this is correct answer  
            optionC: questionData.correctAnswer === 2, // boolean: true if this is correct answer
            optionD: questionData.correctAnswer === 3  // boolean: true if this is correct answer
          },
          // Also store the actual option texts for reference
          optionTexts: {
            optionA: questionData.options?.[0] || "",
            optionB: questionData.options?.[1] || "",
            optionC: questionData.options?.[2] || "",
            optionD: questionData.options?.[3] || ""
          }
        }

        // Save to /home/{contentId}/testManage/{testDocName}/questionSet/questions_{i+1}
        const questionDocRef = doc(questionSetRef, questionDocName)
        await setDoc(questionDocRef, questionPayload)

        console.log(`✅ Question ${i + 1} saved at: /home/${contentId}/testManage/${testDocName}/questionSet/${questionDocName}`)
        console.log(`   - Question Type: ${questionPayload.questionType}`)
        console.log(`   - Image URL: ${questionPayload.questionImageURL ? questionPayload.questionImageURL.substring(0, 50) + '...' : 'NONE'}`)
        console.log(`   - Marks: ${questionPayload.marks}`)
      }

      console.log('✅ All questions saved successfully for test:', testDocName)
      console.log('📋 Path structure used: /home/' + contentId + '/testManage/' + testDocName + '/questionSet/{dynamicData}_question_{N}')

      // Update cache
      const testPayload = {
        id: testDocName,
        testName: questionsData.testName,
        startDate: questionsData.startDate,
        endDate: questionsData.endDate,
        duration: questionsData.duration || 60,
        marks: Number(existingTest?.marks || questionsData.marks) || 0,
        questions: questionsData.questions,
        totalQuestions: questionsData.questions?.length || 0,
        createdAt: new Date(),
        createdBy: questionsData.createdBy || 'admin@nursingexam.com',
        status: 'active'
      }

      // Clear cache to ensure fresh data is loaded
      clearTestCache(contentId)

      return testDocName
    } catch (error) {
      console.error('❌ Error adding test questions:', error)
      throw new Error(`Failed to add test questions: ${error.message}`)
    }
  }

  const createTestMetadata = async (contentId, testData) => {
    try {
      console.log('🔥 Creating test metadata for path /home/{dynamicData}/testManage:', { contentId, testData })

      // Validate inputs
      if (!contentId) {
        throw new Error('ContentId is required')
      }
      if (!testData.testName) {
        throw new Error('Test name is required')
      }

      // Ensure proper data structure as per your specification
      const payload = {
        testName: testData.testName.trim() || "",
        startDate: testData.startDate || new Date(),
        endDate: testData.endDate || new Date(),
        quetionsCount: Number(testData.questionsCount || testData.totalQuestions || 0), // Note: keeping your spelling "quetionsCount"
        marks: Number(testData.marks) || 0, // Store as number instead of string
        duration: Number(testData.duration || 60), // Duration in minutes, default 60
      }

      console.log('🔥 Payload to save at /home/' + contentId + '/testManage (dynamicData=' + contentId + '):', payload)

      // Create the Firestore path: /home/{dynamicData}/testManage/{testNumber}
      // First ensure the dynamic document exists (using contentId as dynamicData)
      const dynamicDocRef = doc(db, 'home', contentId)
      await setDoc(dynamicDocRef, { initialized: true }, { merge: true })

      // Create testManage subcollection and find next test number
      const testManageRef = collection(dynamicDocRef, 'testManage')
      const snapshot = await getDocs(testManageRef)
      let maxTestNumber = 0

      snapshot.docs.forEach(doc => {
        if (doc.id.startsWith('test')) {
          const number = parseInt(doc.id.replace('test', '')) || 0
          if (number > maxTestNumber) {
            maxTestNumber = number
          }
        }
      })

      const nextTestNumber = maxTestNumber + 1
      const testDocName = `test${nextTestNumber}`

      // Save to /home/{contentId}/testManage/test{number}
      const testDocRef = doc(testManageRef, testDocName)
      await setDoc(testDocRef, payload)

      console.log('✅ Test created at path: /home/' + contentId + '/testManage/' + testDocName + ' (dynamicData=' + contentId + ')')

      // Sync testCounts in parent document after creating test
      const actualTestCount = await syncTestCounts(contentId)
      console.log('✅ Synced testCounts to:', actualTestCount, 'after creating test')

      const newTest = { id: testDocName, ...payload }
      setTestCache(prev => {
        const cur = prev.get(contentId) || []
        return new Map(prev).set(contentId, [newTest, ...cur])
      })

      return testDocName
    } catch (error) {
      console.error('❌ Error in createTestMetadata:', error)
      throw new Error(`Failed to create test: ${error.message}`)
    }
  }

  const updateTestMetadata = async (contentId, originalTestName, updatedData) => {
    try {
      console.log('🔥 Updating test metadata at /home/' + contentId + '/testManage (dynamicData=' + contentId + '):', { contentId, originalTestName, updatedData })

      // Find the existing test document
      const existingTests = await getTestQuestions(contentId)
      const existingTest = existingTests.find(test =>
        (test.testName || test.title || test.testTitle) === originalTestName
      )

      if (!existingTest) {
        throw new Error(`Test "${originalTestName}" not found`)
      }

      // Prepare payload according to your data structure
      const payload = {
        testName: updatedData.testName || existingTest.testName || "",
        startDate: updatedData.startDate || existingTest.startDate,
        endDate: updatedData.endDate || existingTest.endDate,
        quetionsCount: Number(updatedData.questionsCount || updatedData.totalQuestions || existingTest.quetionsCount || 0),
        marks: Number(updatedData.marks || existingTest.marks) || 0,
      }

      // Update the document in /home/{contentId}/testManage/{testId}
      const dynamicDocRef = doc(db, 'home', contentId)
      const testDocRef = doc(collection(dynamicDocRef, 'testManage'), existingTest.id)
      await updateDoc(testDocRef, payload)

      console.log('✅ Test updated at path: /home/' + contentId + '/testManage/' + existingTest.id + ' (dynamicData=' + contentId + ')')

      // Update cache
      setTestCache(prev => {
        const cur = prev.get(contentId) || []
        const updatedTests = cur.map(test =>
          test.id === existingTest.id ? { ...test, ...payload } : test
        )
        return new Map(prev).set(contentId, updatedTests)
      })

      return existingTest.id
    } catch (error) {
      console.error('❌ Error in updateTestMetadata:', error)
      throw new Error(`Failed to update test: ${error.message}`)
    }
  }

  const updateTestById = async (testId, updatedData) => {
    try {
      console.log('🔥 Updating test by ID with new structure:', { testId, updatedData })

      // We need to find which contentId this test belongs to
      // For now, we'll use a different approach - update through contentId
      throw new Error('updateTestById deprecated - use updateTestMetadata with contentId instead')

    } catch (error) {
      console.error('❌ Error updating test by ID:', error)
      throw new Error(`Failed to update test: ${error.message}`)
    }
  }

  const updateTestWithQuestions = async (contentId, testId, updatedData) => {
    try {
      console.log('🔥 Updating test with questions at /home/' + contentId + '/testManage/' + testId + ' (dynamicData=' + contentId + '):', updatedData)

      // Update test metadata
      const metadataPayload = {
        testName: updatedData.testName || "",
        startDate: updatedData.startDate || new Date(),
        endDate: updatedData.endDate || new Date(),
        duration: Number(updatedData.duration || 60),
        quetionsCount: Number(updatedData.questions?.length || 0),
        marks: Number(updatedData.marks) || 0,
        updatedAt: new Date()
      }

      // Update the test document
      const dynamicDocRef = doc(db, 'home', contentId)
      const testDocRef = doc(collection(dynamicDocRef, 'testManage'), testId)
      await updateDoc(testDocRef, metadataPayload)

      // If questions are provided, update them too
      if (updatedData.questions && updatedData.questions.length > 0) {
        const questionSetRef = collection(testDocRef, 'questionSet')

        // First, delete all existing questions
        const existingQuestionsSnap = await getDocs(questionSetRef)
        const batch = writeBatch(db)

        existingQuestionsSnap.docs.forEach(doc => {
          batch.delete(doc.ref)
        })
        await batch.commit()

        // Calculate marks per question (equal distribution)
        const totalMarks = parseInt(updatedData.marks) || 0
        const totalQuestions = updatedData.questions.length
        const marksPerQuestion = totalQuestions > 0 ? Math.round(totalMarks / totalQuestions) : 0

        console.log(`📊 Distributing ${totalMarks} marks equally among ${totalQuestions} questions = ${marksPerQuestion} marks per question`)

        // Now add the new questions
        for (let i = 0; i < updatedData.questions.length; i++) {
          const questionData = updatedData.questions[i]
          const questionDocName = `${contentId}_question_${i + 1}` // Use dynamic data (contentId) in question document name

          // DEBUG: Log image handling
          console.log(`🐛 DEBUG updateTestWithQuestions - Q${i + 1}:`, {
            questionImagePreview: questionData.questionImagePreview,
            questionImage: questionData.questionImage,
            questionType: questionData.questionType
          })

          // Upload question image if provided, otherwise preserve existing image URL
          let questionImageURL = null
          if (questionData.questionImage) {
            // New image provided, upload it
            questionImageURL = await uploadQuestionImage(questionData.questionImage, contentId, questionDocName)
          } else if (questionData.questionImagePreview && typeof questionData.questionImagePreview === 'string' && questionData.questionImagePreview.length > 0) {
            // No new image, but existing image URL provided, preserve it
            questionImageURL = questionData.questionImagePreview
            console.log(`  ✅ Preserving image URL: ${questionImageURL}`)
          } else {
            console.log(`  ⚠️  No image for Q${i + 1}: preview="${questionData.questionImagePreview}" (type=${typeof questionData.questionImagePreview})`)
          }

          // Transform question data to match your Firestore structure
          const questionPayload = {
            question: questionData.question || "",
            explanation: questionData.explanation || "",
            marks: marksPerQuestion, // Add marks per question
            questionType: questionData.questionType || 'text', // 'text' or 'image'
            questionImageURL: questionImageURL || null, // Image URL if image question
            options: {
              optionA: questionData.correctAnswer === 0,
              optionB: questionData.correctAnswer === 1,
              optionC: questionData.correctAnswer === 2,
              optionD: questionData.correctAnswer === 3
            },
            optionTexts: {
              optionA: questionData.options?.[0] || "",
              optionB: questionData.options?.[1] || "",
              optionC: questionData.options?.[2] || "",
              optionD: questionData.options?.[3] || ""
            }
          }

          const questionDocRef = doc(questionSetRef, questionDocName)
          await setDoc(questionDocRef, questionPayload)

          console.log(`✅ Question ${i + 1} saved at: /home/${contentId}/testManage/${testId}/questionSet/${questionDocName}`)
          console.log(`   - Question Type: ${questionPayload.questionType}`)
          console.log(`   - Image URL: ${questionPayload.questionImageURL ? questionPayload.questionImageURL.substring(0, 50) + '...' : 'NONE'}`)
          console.log(`   - Marks: ${questionPayload.marks}`)
        }

        console.log(`✅ Updated ${updatedData.questions.length} questions for test ${testId}`)
        console.log('📋 Question path structure used: /home/' + contentId + '/testManage/' + testId + '/questionSet/{dynamicData}_question_{N}')
      }

      // Clear cache to get fresh data
      clearTestCache(contentId)

      console.log('✅ Test updated successfully at /home/' + contentId + '/testManage/' + testId + ' (dynamicData=' + contentId + ')')
      return testId
    } catch (error) {
      console.error('❌ Error updating test with questions:', error)
      throw new Error(`Failed to update test: ${error.message}`)
    }
  }

  const deleteTestQuestions = async (contentId, testId) => {
    try {
      console.log('🔥 Deleting test from /home/' + contentId + '/testManage (dynamicData=' + contentId + '):', { contentId, testId })

      const dynamicDocRef = doc(db, 'home', contentId)
      const testDocRef = doc(collection(dynamicDocRef, 'testManage'), testId)

      // First get all questions to check for any storage references (images, files, etc.)
      const questionSetRef = collection(testDocRef, 'questionSet')
      const questionsSnap = await getDocs(questionSetRef)

      // Delete associated storage files if any exist
      let deletedStorageFiles = 0
      for (const questionDoc of questionsSnap.docs) {
        const questionData = questionDoc.data()

        // Check for image URLs in question or options
        const storageUrls = []
        if (questionData.imageUrl) storageUrls.push(questionData.imageUrl)
        if (questionData.questionImage) storageUrls.push(questionData.questionImage)
        if (questionData.questionImageURL) storageUrls.push(questionData.questionImageURL)

        // Handle options - can be either array or object
        if (questionData.options) {
          if (Array.isArray(questionData.options)) {
            questionData.options.forEach(opt => {
              if (typeof opt === 'object' && opt.imageUrl) storageUrls.push(opt.imageUrl)
            })
          } else if (typeof questionData.options === 'object') {
            // Options is an object with optionA, optionB, etc.
            Object.values(questionData.options).forEach(opt => {
              if (typeof opt === 'object' && opt.imageUrl) storageUrls.push(opt.imageUrl)
            })
          }
        }

        // Delete files from Firebase Storage
        for (const url of storageUrls) {
          try {
            if (url && url.includes('firebase')) {
              await deleteImage(url)
              deletedStorageFiles++
              console.log('🗑️ Deleted storage file:', url)
            }
          } catch (storageError) {
            console.warn('⚠️ Failed to delete storage file:', url, storageError.message)
            // Continue deleting other files even if one fails
          }
        }
      }

      if (deletedStorageFiles > 0) {
        console.log(`✅ Deleted ${deletedStorageFiles} storage files`)
      }

      // Delete all questions from Firestore using batch
      const batch = writeBatch(db)
      questionsSnap.docs.forEach(doc => {
        batch.delete(doc.ref)
      })
      await batch.commit()

      console.log(`✅ Deleted ${questionsSnap.docs.length} questions from questionSet`)

      // Then delete the test document itself
      await deleteDoc(testDocRef)

      console.log('✅ Test deleted successfully from /home/' + contentId + '/testManage/' + testId + ' (dynamicData=' + contentId + ')')

      // Sync testCounts with actual test count in parent document
      const actualTestCount = await syncTestCounts(contentId)
      console.log('✅ Synced testCounts to:', actualTestCount)

      // Clear cache to ensure fresh data on next fetch
      clearTestCache(contentId)

      return true
    } catch (error) {
      console.error('❌ Error deleting test from /home/' + contentId + '/testManage:', error)
      throw new Error(`Failed to delete test: ${error.message}`)
    }
  }

  // ----------------- Realtime Database helpers (updates & alerts + generic) -----------------
  const fetchRealtimeData = async (path) => {
    try {
      const snap = await get(dbRef(database, path))
      const data = snap.val() || {}
      return Object.entries(data).map(([id, val]) => ({ id, ...val }))
    } catch (error) {
      console.error(`Error fetching ${path}:`, error)
      return []
    }
  }

  const addRealtimeData = async (path, data) => {
    try {
      const newRef = push(dbRef(database, path))
      await set(newRef, { ...data, createdAt: Date.now(), createdBy: data.createdBy || 'admin@nursingexam.com' })
      return newRef.key
    } catch (error) {
      console.error(`Error adding ${path}:`, error)
      throw error
    }
  }

  const updateRealtimeData = async (path, id, data) => {
    try {
      await rtdbUpdate(dbRef(database, `${path}/${id}`), data)
    } catch (error) {
      console.error(`Error updating ${path}:`, error)
      throw error
    }
  }

  const deleteRealtimeData = async (path, id) => {
    try {
      await remove(dbRef(database, `${path}/${id}`))
    } catch (error) {
      console.error(`Error deleting ${path}:`, error)
      throw error
    }
  }

  // ---- Updates (Realtime DB) ----
  const getUpdates = async () => {
    try {
      console.log('📋 Fetching updates from Firestore...')
      const updatesRef = collection(db, 'updates')
      const q = query(updatesRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      const updates = []

      snapshot.forEach((doc) => {
        updates.push({
          id: doc.id,
          ...doc.data()
        })
      })

      console.log('✅ Fetched', updates.length, 'updates from Firestore')
      return updates
    } catch (error) {
      console.error('❌ Error fetching updates from Firestore:', error)
      return []
    }
  }

  const addUpdate = async (updateData) => {
    try {
      console.log('🔥 Adding update to Firestore:', updateData)
      const docRef = await addDoc(collection(db, 'updates'), {
        ...updateData,
        createdAt: Date.now(),
        createdBy: updateData.createdBy || 'admin@nursingexam.com'
      })
      console.log('✅ Update added to Firestore with ID:', docRef.id)
      return docRef.id
    } catch (error) {
      console.error('❌ Error adding update to Firestore:', error)
      throw error
    }
  }

  const updateUpdate = async (id, updateData) => {
    try {
      console.log('🔄 Updating update in Firestore:', id, updateData)

      // If updating with a new imageUrl, delete the old one from Storage
      if (updateData.imageUrl) {
        try {
          const docSnap = await getDoc(doc(db, 'updates', id))
          if (docSnap.exists()) {
            const oldData = docSnap.data()
            const oldImageUrl = oldData.imageUrl

            // Delete old image if it exists and is different from new one
            if (oldImageUrl && oldImageUrl !== updateData.imageUrl && oldImageUrl.includes('firebase')) {
              console.log('🗑️ Deleting old image from Storage:', oldImageUrl)
              try {
                await deleteImage(oldImageUrl)
                console.log('✅ Old image deleted from Storage')
              } catch (deleteError) {
                console.warn('⚠️ Could not delete old image (may not exist):', deleteError.message)
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ Could not check for old image:', error.message)
        }
      }

      await updateDoc(doc(db, 'updates', id), {
        ...updateData,
        updatedAt: Date.now(),
        updatedBy: updateData.updatedBy || 'admin@nursingexam.com'
      })
      console.log('✅ Update updated in Firestore')
    } catch (error) {
      console.error('❌ Error updating update in Firestore:', error)
      throw error
    }
  }

  const deleteUpdate = async (id) => {
    try {
      console.log('🗑️ Deleting update from Firestore:', id)

      // Get the document to check for image URL
      const docSnap = await getDoc(doc(db, 'updates', id))
      if (docSnap.exists()) {
        const data = docSnap.data()
        const imageUrl = data.imageUrl

        // Delete image from Storage if it exists
        if (imageUrl && imageUrl.includes('firebase')) {
          console.log('🗑️ Deleting image from Storage:', imageUrl)
          try {
            await deleteImage(imageUrl)
            console.log('✅ Image deleted from Storage')
          } catch (deleteError) {
            console.warn('⚠️ Could not delete image (may not exist):', deleteError.message)
            // Continue with document deletion even if image deletion fails
          }
        }
      }

      // Delete the Firestore document
      await deleteDoc(doc(db, 'updates', id))
      console.log('✅ Update deleted from Firestore')
    } catch (error) {
      console.error('❌ Error deleting update from Firestore:', error)
      throw error
    }
  }

  const sendUpdate = async (id) => {
    try {
      await updateRealtimeData('updates', id, { status: 'sent', sentAt: Date.now() })
    } catch (error) {
      console.error('Error sending update:', error)
      throw error
    }
  }

  const dismissUpdate = async (id) => {
    try {
      await updateRealtimeData('updates', id, { status: 'dismissed', dismissedAt: Date.now() })
    } catch (error) {
      console.error('Error dismissing update:', error)
      throw error
    }
  }

  // ---- Alerts (Realtime DB) ----
  const getAlerts = async () => {
    try {
      console.log('📋 Fetching alerts from Firestore...')

      // Use Firestore as primary source (skip Realtime DB due to permission issues)
      const alertsSnap = await getDocs(query(collection(db, 'alerts'), orderBy('createdAt', 'desc')))
      const alerts = []
      alertsSnap.forEach((doc) => {
        const data = doc.data()
        alerts.push({
          id: doc.id,
          ...data,
          // Ensure timestamps are properly converted
          createdAt: convertFirestoreTimestamp(data.createdAt),
          updatedAt: convertFirestoreTimestamp(data.updatedAt)
        })
      })

      console.log('✅ Successfully fetched', alerts.length, 'alerts from Firestore')
      return alerts
    } catch (error) {
      console.error('❌ Error fetching alerts from Firestore:', error)

      // If Firestore fails, try Realtime Database as fallback
      try {
        console.log('🔄 Trying Realtime Database fallback...')
        const rtdbAlerts = await fetchRealtimeData('alerts')
        if (Array.isArray(rtdbAlerts) && rtdbAlerts.length > 0) {
          console.log('✅ Successfully fetched', rtdbAlerts.length, 'alerts from Realtime DB')
          return rtdbAlerts
        }
        return []
      } catch (rtdbError) {
        console.error('❌ Realtime Database fallback also failed:', rtdbError)
        return []
      }
    }
  }

  const addAlert = async (alertData) => {
    return await addRealtimeData('alerts', alertData)
  }

  const updateAlert = async (id, alertData) => {
    try {
      console.log('📝 Updating alert in Firestore...')

      // Check for old files to delete from Storage
      try {
        const docSnap = await getDoc(doc(db, 'alerts', id))
        if (docSnap.exists()) {
          const oldData = docSnap.data()

          // Delete old image if being replaced with new one
          if (alertData.imageUrl && oldData.imageUrl &&
            oldData.imageUrl !== alertData.imageUrl &&
            oldData.imageUrl.includes('firebase')) {
            console.log('🗑️ Deleting old image from Storage:', oldData.imageUrl)
            try {
              await deleteImage(oldData.imageUrl)
              console.log('✅ Old image deleted from Storage')
            } catch (deleteError) {
              console.warn('⚠️ Could not delete old image (may not exist):', deleteError.message)
            }
          }

          // Delete old PDF if being replaced with new one
          if (alertData.pdfUrl && oldData.pdfUrl &&
            oldData.pdfUrl !== alertData.pdfUrl &&
            oldData.pdfUrl.includes('firebase')) {
            console.log('🗑️ Deleting old PDF from Storage:', oldData.pdfUrl)
            try {
              await deleteImage(oldData.pdfUrl)
              console.log('✅ Old PDF deleted from Storage')
            } catch (deleteError) {
              console.warn('⚠️ Could not delete old PDF (may not exist):', deleteError.message)
            }
          }
        }
      } catch (error) {
        console.warn('⚠️ Could not check for old files:', error.message)
      }

      // Automatically set planStatus based on plan field
      const planStatus = alertData.plan && alertData.plan.trim() !== '' && alertData.plan.toLowerCase() !== 'free'
        ? 'paid'
        : 'free'

      const updatedData = {
        ...alertData,
        planStatus,
        updatedAt: new Date(),
        updatedBy: alertData.updatedBy || 'admin@nursingexam.com'
      }

      // Update in Firestore (primary storage)
      await updateDoc(doc(db, 'alerts', id), updatedData)
      console.log('✅ Alert updated in Firestore')

      // Realtime Database mirroring disabled - Firestore is the primary storage
    } catch (error) {
      console.error('❌ Error updating alert:', error)
      throw error
    }
  }

  const deleteAlert = async (id) => {
    try {
      console.log('🗑️ Deleting alert from Firestore...')

      // Get the document to check for Storage files
      const docSnap = await getDoc(doc(db, 'alerts', id))
      if (docSnap.exists()) {
        const data = docSnap.data()

        // Delete image from Storage if it exists
        if (data.imageUrl && data.imageUrl.includes('firebase')) {
          console.log('🗑️ Deleting image from Storage:', data.imageUrl)
          try {
            await deleteImage(data.imageUrl)
            console.log('✅ Image deleted from Storage')
          } catch (deleteError) {
            console.warn('⚠️ Could not delete image (may not exist):', deleteError.message)
          }
        }

        // Delete PDF from Storage if it exists
        if (data.pdfUrl && data.pdfUrl.includes('firebase')) {
          console.log('🗑️ Deleting PDF from Storage:', data.pdfUrl)
          try {
            await deleteImage(data.pdfUrl)
            console.log('✅ PDF deleted from Storage')
          } catch (deleteError) {
            console.warn('⚠️ Could not delete PDF (may not exist):', deleteError.message)
          }
        }
      }

      // Delete the Firestore document
      await deleteDoc(doc(db, 'alerts', id))
      console.log('✅ Alert deleted from Firestore')
      // Realtime Database mirroring disabled - Firestore is the primary storage
      return true
    } catch (error) {
      console.error('❌ Error deleting alert from Firestore:', error)
      throw error
    }
  }

  const sendAlert = async (id) => {
    try {
      await updateRealtimeData('alerts', id, { status: 'sent', sentAt: Date.now() })
    } catch (error) {
      console.error('Error sending alert:', error)
      throw error
    }
  }

  const dismissAlert = async (id) => {
    try {
      await updateRealtimeData('alerts', id, { status: 'dismissed', dismissedAt: Date.now() })
    } catch (error) {
      console.error('Error dismissing alert:', error)
      throw error
    }
  }

  // Also store alerts in Firestore if needed
  const addAlertToFirestore = async (alertData) => {
    try {
      const docRef = await addDoc(collection(db, 'alerts'), {
        ...alertData,
        createdAt: new Date(),
        createdBy: alertData.createdBy || 'admin@nursingexam.com'
      })
      return docRef.id
    } catch (error) {
      console.error('Error adding alert to Firestore:', error)
      throw error
    }
  }

  // Create in Realtime DB and mirror to Firestore with the same ID
  const addAlertBoth = async (alertData) => {
    try {
      console.log('📝 Creating alert in Firestore...')

      // Automatically set planStatus based on plan field
      const planStatus = alertData.plan && alertData.plan.trim() !== '' && alertData.plan.toLowerCase() !== 'free'
        ? 'paid'
        : 'free'

      // Create in Firestore first (primary storage)
      const docRef = await addDoc(collection(db, 'alerts'), {
        ...alertData,
        planStatus,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: alertData.createdBy || 'admin@nursingexam.com'
      })

      console.log('✅ Alert created in Firestore with ID:', docRef.id)

      // Realtime Database mirroring disabled - Firestore is the primary storage
      // If you need Realtime Database, configure permissions in Firebase Console

      return docRef.id
    } catch (error) {
      console.error('❌ Error creating alert in Firestore:', error)
      throw error
    }
  }

  // ----------------- Online Tests -----------------
  const getOnlineTests = async () => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        return []
      }
      const onlineTestsSnapshot = await getDocs(query(collection(db, 'onlineTests'), orderBy('createdAt', 'desc')))
      return onlineTestsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      console.error('Error fetching online tests:', error)
      return []
    }
  }

  const deleteTest = async (id) => {
    try {
      console.log('🔥 Deleting online test:', id)

      // First get all questions in the questionSet subcollection
      const questionSetRef = collection(db, 'onlineTests', id, 'questionSet')
      const questionsSnap = await getDocs(questionSetRef)

      // Delete associated storage files if any exist
      let deletedStorageFiles = 0
      for (const questionDoc of questionsSnap.docs) {
        const questionData = questionDoc.data()

        // Check for image URLs in question
        const storageUrls = []
        if (questionData.questionImageURL) storageUrls.push(questionData.questionImageURL)
        if (questionData.imageUrl) storageUrls.push(questionData.imageUrl)

        // Check options if they have images
        if (questionData.optionImages) {
          Object.values(questionData.optionImages).forEach(url => {
            if (url) storageUrls.push(url)
          })
        }

        // Delete files from Firebase Storage
        for (const url of storageUrls) {
          try {
            if (url && url.includes('firebase')) {
              await deleteImage(url)
              deletedStorageFiles++
              console.log('🗑️ Deleted storage file:', url)
            }
          } catch (storageError) {
            console.warn('⚠️ Failed to delete storage file:', url, storageError.message)
            // Continue deleting other files even if one fails
          }
        }
      }

      if (deletedStorageFiles > 0) {
        console.log(`✅ Deleted ${deletedStorageFiles} storage files from online test`)
      }

      // Delete all questions from questionSet subcollection
      const batch = writeBatch(db)
      questionsSnap.docs.forEach(doc => {
        batch.delete(doc.ref)
      })
      await batch.commit()

      console.log(`✅ Deleted ${questionsSnap.docs.length} questions from questionSet`)

      // Delete the test document itself
      await deleteDocument('onlineTests', id)

      console.log('✅ Online test deleted successfully:', id)
      return true
    } catch (error) {
      console.error('❌ Error deleting online test:', error)
      throw error
    }
  }

  const addOnlineTest = async (testData) => {
    try {
      if (!db) {
        console.error('Firestore not initialized')
        throw new Error('Firestore not initialized')
      }
      // Get existing tests to determine next test number
      const existingTests = await getDocs(collection(db, 'onlineTests'))
      const testNumbers = existingTests.docs
        .map(doc => doc.id)
        .filter(id => id.startsWith('test'))
        .map(id => parseInt(id.replace('test', '')))
        .filter(num => !isNaN(num))

      const nextTestNumber = testNumbers.length > 0 ? Math.max(...testNumbers) + 1 : 1
      const testId = `test${nextTestNumber}`

      // Create document with custom ID
      const docRef = doc(db, 'onlineTests', testId)
      await setDoc(docRef, {
        testName: testData.testName,
        startDate: testData.startDate,
        endDate: testData.endDate,
        duration: testData.duration,
        questionCount: testData.questionCount || 0,
        totalMarks: testData.totalMarks || "",
        totalQuestions: testData.totalQuestions || testData.questionCount || 0,
        questionIds: testData.questionIds || [],
        createdAt: testData.createdAt || new Date(),
        updatedAt: testData.updatedAt || new Date()
      })

      return { id: testId, ...testData }
    } catch (error) {
      console.error('Error adding online test:', error)
      throw error
    }
  }

  const updateTestQuestions = async (testId, questionIds) => {
    try {
      const testRef = doc(db, 'onlineTests', testId)
      await updateDoc(testRef, {
        questionIds: questionIds,
        totalQuestions: questionIds.length,
        updatedAt: new Date()
      })
      console.log(`Test questions updated successfully - Total: ${questionIds.length} questions`)
    } catch (error) {
      console.error('Error updating test questions:', error)
      throw error
    }
  }

  // Helper function to upload question image to Firebase Storage
  const uploadQuestionImage = async (imageFile, testId, questionId) => {
    try {
      if (!imageFile) return null

      // Create a unique path for the image
      const imagePath = `questionImages/${testId}/${questionId}_${Date.now()}`
      const imageRef = storageRef(storage, imagePath)

      // Upload the image
      await uploadBytes(imageRef, imageFile)

      // Get the download URL
      const downloadURL = await getDownloadURL(imageRef)

      console.log('✅ Question image uploaded:', downloadURL)
      return downloadURL
    } catch (error) {
      console.error('❌ Error uploading question image:', error)
      throw error
    }
  }

  // Add questions to online test subcollection with marks distribution
  const addOnlineTestQuestions = async (testId, questionsData) => {
    try {
      console.log('🔥 Adding questions to /onlineTests/' + testId + '/questionSet/', questionsData)

      if (!db) {
        throw new Error('Firestore not initialized')
      }

      // First, get the test data to retrieve total marks
      const testRef = doc(db, 'onlineTests', testId)
      const testSnap = await getDoc(testRef)

      if (!testSnap.exists()) {
        throw new Error('Test not found')
      }

      const testData = testSnap.data()
      const totalMarks = parseInt(testData.totalMarks) || 0

      // Get existing questions to calculate new total count
      const questionSetRef = collection(db, 'onlineTests', testId, 'questionSet')
      const existingQuestionsSnap = await getDocs(questionSetRef)
      const existingQuestionCount = existingQuestionsSnap.docs.length

      // Calculate new total question count
      const newTotalQuestions = existingQuestionCount + questionsData.length

      // Calculate marks per question (equal distribution)
      const marksPerQuestion = newTotalQuestions > 0 ? Math.round(totalMarks / newTotalQuestions) : 0

      console.log(`📊 Distributing ${totalMarks} marks equally among ${newTotalQuestions} questions = ${marksPerQuestion} marks per question`)

      // Update marks for all existing questions first
      if (existingQuestionCount > 0 && marksPerQuestion > 0) {
        console.log('🔄 Updating marks for existing questions...')
        const batch = writeBatch(db)

        existingQuestionsSnap.docs.forEach(doc => {
          batch.update(doc.ref, {
            marks: marksPerQuestion,
            updatedAt: new Date()
          })
        })

        await batch.commit()
        console.log(`✅ Updated marks for ${existingQuestionCount} existing questions`)
      }

      const questionIds = []

      // Process each new question
      for (let i = 0; i < questionsData.length; i++) {
        const questionData = questionsData[i]
        const questionId = `question_${existingQuestionCount + i + 1}`

        // Upload question image if provided, otherwise preserve existing image URL
        let questionImageURL = null
        if (questionData.questionImage) {
          // New image provided, upload it
          questionImageURL = await uploadQuestionImage(questionData.questionImage, testId, questionId)
        } else if (questionData.questionImagePreview && typeof questionData.questionImagePreview === 'string' && questionData.questionImagePreview.length > 0) {
          // No new image, but existing image URL provided, preserve it
          questionImageURL = questionData.questionImagePreview
        }

        // Create the question document with the correct structure including marks
        const questionPayload = {
          questionName: questionData.question || questionData.questionName || "",
          explanation: questionData.explanation || "",
          marks: marksPerQuestion, // Add marks per question
          questionType: questionData.questionType || 'text', // 'text' or 'image'
          questionImageURL: questionImageURL || null, // Image URL if image question
          optionA: questionData.correctAnswer === 0, // true if correct
          optionB: questionData.correctAnswer === 1, // true if correct
          optionC: questionData.correctAnswer === 2, // true if correct
          optionD: questionData.correctAnswer === 3, // true if correct
          // Store option texts for reference
          optionTexts: {
            optionA: questionData.options?.[0] || "",
            optionB: questionData.options?.[1] || "",
            optionC: questionData.options?.[2] || "",
            optionD: questionData.options?.[3] || ""
          },
          createdAt: new Date(),
          updatedAt: new Date()
        }

        // Save to /onlineTests/{testId}/questionSet/{questionId}
        const questionRef = doc(db, 'onlineTests', testId, 'questionSet', questionId)
        await setDoc(questionRef, questionPayload)

        questionIds.push(questionId)

        console.log(`✅ Question ${existingQuestionCount + i + 1} saved at: /onlineTests/${testId}/questionSet/${questionId} with ${marksPerQuestion} marks`)
      }

      // Get all question IDs (existing + new) for updating test document
      const allQuestionsSnap = await getDocs(questionSetRef)
      const allQuestionIds = allQuestionsSnap.docs.map(doc => doc.id)

      // Update the test document with question IDs and counts
      await updateTestQuestions(testId, allQuestionIds)

      console.log('✅ All questions added successfully to online test:', testId)
      console.log(`📊 Total questions: ${newTotalQuestions}, Marks per question: ${marksPerQuestion}`)
      return questionIds

    } catch (error) {
      console.error('❌ Error adding online test questions:', error)
      throw new Error(`Failed to add questions: ${error.message}`)
    }
  }

  // Get questions from online test subcollection
  const getOnlineTestQuestions = async (testId) => {
    try {
      console.log('📋 Getting questions from /onlineTests/' + testId + '/questionSet/')

      if (!db) {
        throw new Error('Firestore not initialized')
      }

      const questionSetRef = collection(db, 'onlineTests', testId, 'questionSet')
      const questionsSnap = await getDocs(questionSetRef)

      const questions = []
      questionsSnap.forEach((doc) => {
        const data = doc.data()

        // Transform back to expected format
        let correctAnswer = 0 // default
        if (data.optionA) correctAnswer = 0
        else if (data.optionB) correctAnswer = 1
        else if (data.optionC) correctAnswer = 2
        else if (data.optionD) correctAnswer = 3

        questions.push({
          id: doc.id,
          question: data.questionName || "",
          explanation: data.explanation || "",
          marks: data.marks || 0, // Include marks in the returned data
          questionType: data.questionType || 'text', // Include question type
          questionImageURL: data.questionImageURL || null, // Include image URL
          options: [
            data.optionTexts?.optionA || "Option A",
            data.optionTexts?.optionB || "Option B",
            data.optionTexts?.optionC || "Option C",
            data.optionTexts?.optionD || "Option D"
          ],
          correctAnswer: correctAnswer,
          // Keep original structure for editing
          questionName: data.questionName || "",
          optionA: data.optionA || false,
          optionB: data.optionB || false,
          optionC: data.optionC || false,
          optionD: data.optionD || false
        })
      })

      // Sort by question number
      questions.sort((a, b) => {
        const numA = parseInt(a.id.split('_')[1]) || 0
        const numB = parseInt(b.id.split('_')[1]) || 0
        return numA - numB
      })

      console.log(`📊 Found ${questions.length} questions in online test ${testId}`)
      return questions

    } catch (error) {
      console.error('❌ Error getting online test questions:', error)
      return []
    }
  }

  // Delete a question from online test with marks redistribution
  const deleteOnlineTestQuestion = async (testId, questionId) => {
    try {
      console.log('🗑️ Deleting question:', testId, questionId)

      // First, get the test data to retrieve total marks
      const testRef = doc(db, 'onlineTests', testId)
      const testSnap = await getDoc(testRef)

      if (!testSnap.exists()) {
        throw new Error('Test not found')
      }

      const testData = testSnap.data()
      const totalMarks = parseInt(testData.totalMarks) || 0

      // Delete the question
      const questionRef = doc(db, 'onlineTests', testId, 'questionSet', questionId)
      await deleteDoc(questionRef)

      // Get remaining questions
      const questionSetRef = collection(db, 'onlineTests', testId, 'questionSet')
      const remainingQuestionsSnap = await getDocs(questionSetRef)
      const remainingQuestionCount = remainingQuestionsSnap.docs.length

      // Recalculate marks per question for remaining questions
      const marksPerQuestion = remainingQuestionCount > 0 ? Math.round(totalMarks / remainingQuestionCount) : 0

      console.log(`📊 Redistributing ${totalMarks} marks among ${remainingQuestionCount} remaining questions = ${marksPerQuestion} marks per question`)

      // Update marks for all remaining questions
      if (remainingQuestionCount > 0 && marksPerQuestion > 0) {
        const batch = writeBatch(db)

        remainingQuestionsSnap.docs.forEach(doc => {
          batch.update(doc.ref, {
            marks: marksPerQuestion,
            updatedAt: new Date()
          })
        })

        await batch.commit()
        console.log(`✅ Updated marks for ${remainingQuestionCount} remaining questions`)
      }

      // Update test document with new question count
      const remainingQuestionIds = remainingQuestionsSnap.docs.map(doc => doc.id)
      await updateTestQuestions(testId, remainingQuestionIds)

      console.log('✅ Question deleted and marks redistributed successfully')

    } catch (error) {
      console.error('❌ Error deleting question:', error)
      throw new Error(`Failed to delete question: ${error.message}`)
    }
  }

  // Update a single question in online test
  const updateOnlineTestQuestion = async (testId, questionId, questionData) => {
    try {
      console.log('🔄 Updating question:', testId, questionId, questionData)

      // Get old question data to check for images to delete
      const questionRef = doc(db, 'onlineTests', testId, 'questionSet', questionId)
      const oldQuestionSnap = await getDoc(questionRef)

      if (oldQuestionSnap.exists()) {
        const oldData = oldQuestionSnap.data()

        // Check if image URLs are being replaced
        const oldImageUrls = []
        const newImageUrls = []

        if (oldData.questionImageURL) oldImageUrls.push(oldData.questionImageURL)
        if (oldData.imageUrl) oldImageUrls.push(oldData.imageUrl)

        if (questionData.questionImageURL) newImageUrls.push(questionData.questionImageURL)
        if (questionData.imageUrl) newImageUrls.push(questionData.imageUrl)

        // Delete old images if they're being replaced with new ones
        for (const oldUrl of oldImageUrls) {
          if (oldUrl && !newImageUrls.includes(oldUrl) && oldUrl.includes('firebase')) {
            try {
              await deleteImage(oldUrl)
              console.log('🗑️ Deleted old question image from storage:', oldUrl)
            } catch (err) {
              console.warn('⚠️ Failed to delete old image:', err.message)
            }
          }
        }
      }

      const questionPayload = {
        questionName: questionData.questionName || questionData.question || "",
        explanation: questionData.explanation || "",
        optionA: questionData.optionA !== undefined ? questionData.optionA : (questionData.correctAnswer === 0),
        optionB: questionData.optionB !== undefined ? questionData.optionB : (questionData.correctAnswer === 1),
        optionC: questionData.optionC !== undefined ? questionData.optionC : (questionData.correctAnswer === 2),
        optionD: questionData.optionD !== undefined ? questionData.optionD : (questionData.correctAnswer === 3),
        optionTexts: questionData.optionTexts || {
          optionA: questionData.options?.[0] || "",
          optionB: questionData.options?.[1] || "",
          optionC: questionData.options?.[2] || "",
          optionD: questionData.options?.[3] || ""
        },
        updatedAt: new Date()
      }

      await updateDoc(questionRef, questionPayload)

      console.log('✅ Question updated successfully')
      return true

    } catch (error) {
      console.error('❌ Error updating online test question:', error)
      throw error
    }
  }

  // Delete questions from online test
  const deleteOnlineTestQuestions = async (testId) => {
    try {
      console.log('🗑️ Deleting all questions from /onlineTests/' + testId + '/questionSet/')

      const questionSetRef = collection(db, 'onlineTests', testId, 'questionSet')
      const questionsSnap = await getDocs(questionSetRef)

      // Delete associated storage files first
      let deletedStorageFiles = 0
      for (const questionDoc of questionsSnap.docs) {
        const questionData = questionDoc.data()

        // Collect all storage URLs
        const storageUrls = []
        if (questionData.questionImageURL) storageUrls.push(questionData.questionImageURL)
        if (questionData.imageUrl) storageUrls.push(questionData.imageUrl)

        if (questionData.optionImages) {
          Object.values(questionData.optionImages).forEach(url => {
            if (url) storageUrls.push(url)
          })
        }

        // Delete files from storage
        for (const url of storageUrls) {
          if (url && url.includes('firebase')) {
            try {
              await deleteImage(url)
              deletedStorageFiles++
              console.log('🗑️ Deleted storage file:', url)
            } catch (err) {
              console.warn('⚠️ Failed to delete storage file:', err.message)
            }
          }
        }
      }

      if (deletedStorageFiles > 0) {
        console.log(`✅ Deleted ${deletedStorageFiles} storage files`)
      }

      // Delete questions from Firestore
      const batch = writeBatch(db)
      questionsSnap.docs.forEach(doc => {
        batch.delete(doc.ref)
      })
      await batch.commit()

      // Update test document
      await updateTestQuestions(testId, [])

      console.log(`✅ Deleted ${questionsSnap.docs.length} questions from online test ${testId}`)
      return true

    } catch (error) {
      console.error('❌ Error deleting online test questions:', error)
      throw error
    }
  }

  // ----------------- Leaderboard / Analytics -----------------
  const getLeaderboard = async () => {
    try {
      console.log('📋 Fetching leaderboard data from Firestore...')
      if (!db) {
        console.error('Firestore not initialized')
        return []
      }

      // Fetch all documents from leaderboard collection
      const leaderboardRef = collection(db, 'leaderboard')
      const snapshot = await getDocs(leaderboardRef)

      const leaderboardData = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        leaderboardData.push({
          id: doc.id,
          email: data.email || '',
          name: data.name || '',
          onlineTestMarks: data.onlineTestMarks || 0,
          offlineTestMarks: data.offlineTestMarks || 0,
          profileImg: data.profileImg || '',
          rank: data.rank || 0,
          totalMarks: data.totalMarks || 0,
          submitedDate: convertFirestoreTimestamp(data.submitedDate),
          weeklyMarks: data.weeklyMarks || 0,
          monthlyMarks: data.monthlyMarks || 0,
          // Add any additional fields that might exist
          country: data.country || '',
          district: data.district || '',
          state: data.state || '',
          mobileNumber: data.mobileNumber || ''
        })
      })

      console.log('✅ Successfully fetched', leaderboardData.length, 'leaderboard entries')
      console.log('📊 Sample entry:', leaderboardData[0] || 'No entries found')

      return leaderboardData
    } catch (error) {
      console.error('❌ Error fetching leaderboard:', error)
      return []
    }
  }

  const deleteFromLeaderboard = async (userId) => {
    try {
      console.log('🗑️ Deleting user from leaderboard:', userId)
      if (!db) {
        console.error('Firestore not initialized')
        throw new Error('Firestore not initialized')
      }

      // Delete from leaderboard collection using the userId (which is the email)
      const leaderboardDocRef = doc(db, 'leaderboard', userId)
      await deleteDoc(leaderboardDocRef)

      console.log('✅ Successfully deleted user from leaderboard:', userId)
      return true
    } catch (error) {
      console.error('❌ Error deleting from leaderboard:', error)
      throw error
    }
  }

  const updateLeaderboard = async () => {
    try {
      const users = await getUsers()
      const results = await getResults()

      const userScores = users.map(user => {
        const userResults = results.filter(r => r.userId === user.id)

        // Basic metrics
        const totalScore = userResults.reduce((s, r) => {
          const score = parseInt(r.obtainedMarks || r.marksObtained || r.score || r.totalMarks || 0)
          return s + score
        }, 0)
        const totalPossibleMarks = userResults.reduce((s, r) => s + (parseInt(r.totalMarks) || 0), 0)
        const totalExams = userResults.length
        const averageScore = totalExams > 0 ? Math.round(totalScore / totalExams) : 0
        const overallPercentage = totalPossibleMarks > 0 ? Math.round((totalScore / totalPossibleMarks) * 100) : 0

        // Advanced metrics for toppers calculation
        const passedExams = userResults.filter(r => {
          const percentage = r.totalMarks > 0 ? (r.score / r.totalMarks) * 100 : 0
          return percentage >= 60 // Consider 60% as passing
        }).length

        const successRate = totalExams > 0 ? Math.round((passedExams / totalExams) * 100) : 0

        // Recent performance (last 30 days)
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

        const recentResults = userResults.filter(r => {
          const resultDate = convertFirestoreTimestamp(r.completedAt) || convertFirestoreTimestamp(r.createdAt)
          return resultDate && resultDate >= thirtyDaysAgo
        })

        const recentScore = recentResults.reduce((s, r) => s + (r.score || 0), 0)
        const recentExams = recentResults.length
        const recentAverage = recentExams > 0 ? Math.round(recentScore / recentExams) : 0

        // Calculate streak (consecutive days with tests)
        const sortedResults = userResults
          .map(r => convertFirestoreTimestamp(r.completedAt) || convertFirestoreTimestamp(r.createdAt))
          .filter(date => date)
          .sort((a, b) => b - a)

        let currentStreak = 0
        let lastDate = null

        for (const date of sortedResults) {
          const daysDiff = lastDate ? Math.floor((lastDate - date) / (1000 * 60 * 60 * 24)) : 0
          if (lastDate === null || daysDiff <= 1) {
            currentStreak++
            lastDate = date
          } else {
            break
          }
        }

        // Toppers Score Calculation (weighted formula)
        const toppersScore = calculateToppersScore({
          averageScore,
          overallPercentage,
          successRate,
          totalExams,
          recentAverage,
          currentStreak,
          recentExams
        })

        // Determine topper category
        const topperCategory = getTopperCategory(toppersScore, overallPercentage, successRate)

        return {
          ...user,
          totalScore,
          onlineTestMarks: totalScore, // Consistent field name for UI
          totalMarks: totalPossibleMarks, // Consistent field name
          totalExams,
          averageScore,
          overallPercentage,
          passedExams,
          successRate,
          recentAverage,
          recentExams,
          currentStreak,
          toppersScore,
          topperCategory,
          lastActive: sortedResults[0] || user.createdAt,
          updatedAt: new Date()
        }
      })

      // Sort by toppers score (descending)
      const sortedUsers = userScores.sort((a, b) => {
        // Primary: Toppers Score
        if (b.toppersScore !== a.toppersScore) {
          return b.toppersScore - a.toppersScore
        }
        // Secondary: Overall Percentage
        if (b.overallPercentage !== a.overallPercentage) {
          return b.overallPercentage - a.overallPercentage
        }
        // Tertiary: Total Exams (more experience)
        return b.totalExams - a.totalExams
      })

      // Assign ranks and save to Firestore
      const batch = writeBatch(db)
      sortedUsers.forEach((user, index) => {
        const userWithRank = {
          ...user,
          rank: index + 1,
          isTopThree: index < 3,
          isTopTen: index < 10
        }
        batch.set(doc(db, 'leaderboard', user.id), userWithRank)
      })

      await batch.commit()
      console.log('🏆 Leaderboard updated with toppers logic')
    } catch (error) {
      console.error('Error updating leaderboard:', error)
      throw error
    }
  }

  // Helper function to calculate toppers score
  const calculateToppersScore = (metrics) => {
    const {
      averageScore,
      overallPercentage,
      successRate,
      totalExams,
      recentAverage,
      currentStreak,
      recentExams
    } = metrics

    // Weighted scoring formula
    let score = 0

    // Base score from performance (40% weight)
    score += (overallPercentage * 0.4)

    // Success rate bonus (25% weight)
    score += (successRate * 0.25)

    // Experience bonus (15% weight) - logarithmic to prevent dominance
    const experienceBonus = Math.min(Math.log10(totalExams + 1) * 20, 30)
    score += (experienceBonus * 0.15)

    // Recent performance bonus (15% weight)
    if (recentExams > 0) {
      const recentPercentage = (recentAverage / 100) * 100 // Normalize if needed
      score += (recentPercentage * 0.15)
    }

    // Streak bonus (5% weight)
    const streakBonus = Math.min(currentStreak * 2, 20) // Max 20 points for streak
    score += (streakBonus * 0.05)

    return Math.round(score)
  }

  // Helper function to determine topper category
  const getTopperCategory = (toppersScore, overallPercentage, successRate) => {
    if (toppersScore >= 90 && overallPercentage >= 85 && successRate >= 80) {
      return 'LEGEND' // 🏆
    } else if (toppersScore >= 80 && overallPercentage >= 75 && successRate >= 70) {
      return 'CHAMPION' // 👑
    } else if (toppersScore >= 70 && overallPercentage >= 65 && successRate >= 60) {
      return 'EXPERT' // ⭐
    } else if (toppersScore >= 60 && overallPercentage >= 55) {
      return 'ADVANCED' // 🎯
    } else if (toppersScore >= 50) {
      return 'INTERMEDIATE' // 📈
    } else {
      return 'BEGINNER' // 🌱
    }
  }

  // ----------------- Users -----------------
  const getUserDetails = async (userId) => {
    try {
      console.log('🔍 Fetching user details for:', userId)

      // Try to get user from Firestore users collection
      const userRef = doc(db, 'users', userId)
      const userSnap = await getDoc(userRef)

      if (userSnap.exists()) {
        const userData = userSnap.data()
        return {
          id: userId,
          name: userData.name || userData.displayName || '',
          email: userData.email || '',
          createdAt: convertFirestoreTimestamp(userData.createdAt) || new Date(),
          ...userData
        }
      }

      // If not found in Firestore, try Realtime Database
      const realtimeData = await fetchRealtimeData(`users/${userId}`)
      if (realtimeData) {
        return {
          id: userId,
          name: realtimeData.name || realtimeData.displayName || '',
          email: realtimeData.email || '',
          createdAt: realtimeData.createdAt || new Date(),
          ...realtimeData
        }
      }

      // If still not found, return basic info with email as name
      return {
        id: userId,
        name: userId.includes('@') ? userId.split('@')[0] : userId,
        email: userId.includes('@') ? userId : '',
        createdAt: new Date()
      }
    } catch (error) {
      console.error('Error fetching user details:', error)
      // Return basic user info even if fetch fails
      return {
        id: userId,
        name: userId.includes('@') ? userId.split('@')[0] : userId,
        email: userId.includes('@') ? userId : '',
        createdAt: new Date()
      }
    }
  }

  const getUserTestResults = async (userId) => {
    try {
      console.log('📊 Fetching test results for user:', userId)

      const results = {
        online: [],
        offline: []
      }

      // Fetch online test results from Firestore user subcollection
      try {
        // We check BOTH 'onlineTests' and 'onlineTest' to be robust against inconsistencies
        const collectionPaths = ['onlineTests', 'onlineTest']

        for (const path of collectionPaths) {
          const onlineTestsRef = collection(db, 'users', userId, path)
          const onlineSnapshot = await getDocs(onlineTestsRef)

          onlineSnapshot.docs.forEach(doc => {
            const data = doc.data()
            // Avoid duplicate entries if both collections exist and have same docs
            if (results.online.some(r => r.id === doc.id)) return

            const convertedSubmitedDate = convertFirestoreTimestamp(data.submitedDate) ||
              convertFirestoreTimestamp(data.submittedDate) ||
              convertFirestoreTimestamp(data.submittedAt)
            const completedAt = convertedSubmitedDate || convertFirestoreTimestamp(data.createdAt) || new Date()

            results.online.push({
              id: doc.id,
              testName: data.testName || 'Untitled Test',
              score: parseInt(data.obtainedMarks || data.marksObtained || data.score || data.totalMarks || 0),
              totalMarks: parseInt(data.totalMarks) || 0,
              totalQuestions: data.totalQuestions || 0,
              timeTaken: data.timeTaken || 0,
              percentage: data.totalMarks ? Math.round((parseInt(data.totalMarks) / 100) * 100) || 0 : 0,
              status: data.status || 'completed',
              completedAt: completedAt,
              duration: data.duration || 0,
              category: data.category || 'General',
              submitedDate: convertedSubmitedDate,
              ...data
            })
          })
        }
      } catch (error) {
        console.error('Error fetching online test results:', error)
      }

      // Fetch offline test results from Firestore subcollection
      try {
        const offlineTestsRef = collection(db, 'users', userId, 'offlineTests')
        const offlineSnapshot = await getDocs(offlineTestsRef)

        offlineSnapshot.docs.forEach(doc => {
          const data = doc.data()
          console.log('🔍 Raw offline test data:', data)
          console.log('🔍 submitedDate raw:', data.submitedDate)
          console.log('🔍 submitedDate converted:', convertFirestoreTimestamp(data.submitedDate))

          const convertedSubmitedDate = convertFirestoreTimestamp(data.submitedDate) ||
            convertFirestoreTimestamp(data.submittedDate) ||
            convertFirestoreTimestamp(data.submittedAt)
          const completedAt = convertedSubmitedDate || convertFirestoreTimestamp(data.createdAt) || new Date()

          // Create a proper test name from homeContentName or use testName as fallback
          const displayTestName = data.homeContentName
            ? `${data.homeContentName.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Test`
            : data.testName || 'Untitled Test'

          results.offline.push({
            id: doc.id,
            testName: displayTestName,
            score: data.totalMarks || 0, // Assuming score is stored as totalMarks
            totalMarks: data.totalMarks || 0,
            totalQuestions: data.totalQuestions || 0,
            timeTaken: data.timeTaken || 0,
            percentage: data.totalMarks ? Math.round((data.totalMarks / 100) * 100) || 0 : 0,
            status: data.status || 'completed',
            completedAt: completedAt,
            duration: data.duration || 0,
            category: data.homeContentName || 'General',
            homeContentName: data.homeContentName,
            submitedDate: convertedSubmitedDate,
            ...data
          })
        })
      } catch (error) {
        console.error('Error fetching offline test results:', error)
      }

      // Sort results by completion date (newest first)
      results.online.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
      results.offline.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))

      console.log('📊 User test results:', results)
      return results
    } catch (error) {
      console.error('Error fetching user test results:', error)
      return { online: [], offline: [] }
    }
  }

  // ----------------- Analytics -----------------
  const getAnalytics = async () => {
    try {
      console.log('📊 Loading analytics data...')
      
      if (!db) {
        throw new Error('Firestore not initialized')
      }

      // Get counts directly from server without downloading documents
      const [
        usersSnap, 
        questionsSnap, 
        onlineTestsSnap, 
        updatesSnap, 
        alertsSnap, 
        homeContentSnap
      ] = await Promise.all([
        getCountFromServer(collection(db, 'users')).catch(() => ({ data: () => ({ count: 0 }) })),
        getCountFromServer(collection(db, 'questions')).catch(() => ({ data: () => ({ count: 0 }) })),
        getCountFromServer(collection(db, 'onlineTests')).catch(() => ({ data: () => ({ count: 0 }) })),
        getCountFromServer(collection(db, 'updates')).catch(() => ({ data: () => ({ count: 0 }) })),
        getCountFromServer(collection(db, 'alerts')).catch(() => ({ data: () => ({ count: 0 }) })),
        getCountFromServer(collection(db, 'homeContent')).catch(() => ({ data: () => ({ count: 0 }) }))
      ])

      const totalUsers = usersSnap.data().count
      const totalQuestions = questionsSnap.data().count
      const totalOnlineTests = onlineTestsSnap.data().count
      const totalUpdates = updatesSnap.data().count
      const totalNotes = alertsSnap.data().count
      const totalOfflineTests = homeContentSnap.data().count

      // Calculate daily and monthly active users (mock data for now)
      const dailyActiveUsers = Math.floor(totalUsers * 0.3) // 30% of users active daily
      const monthlyActiveUsers = Math.floor(totalUsers * 0.7) // 70% of users active monthly

      const analytics = {
        totalUsers,
        totalQuestions,
        totalOnlineTests,
        totalOfflineTests,
        totalUpdates,
        totalNotes,
        dailyActiveUsers,
        monthlyActiveUsers
      }

      console.log('📊 Analytics data loaded:', analytics)
      return analytics
    } catch (error) {
      console.error('Error loading analytics:', error)
      // Return default values if there's an error
      return {
        totalUsers: 0,
        totalQuestions: 0,
        totalOnlineTests: 0,
        totalOfflineTests: 0,
        totalUpdates: 0,
        totalNotes: 0,
        dailyActiveUsers: 0,
        monthlyActiveUsers: 0
      }
    }
  }

  // ----------------- Online Quiz Results -----------------
  const getOnlineQuizResults = async () => {
    try {
      console.log('📝 Loading online quiz results...')

      // Get all users first
      const usersRef = collection(db, 'users')
      const usersSnapshot = await getDocs(usersRef)

      let allQuizResults = []

      // For each user, get their onlineTest submissions
      for (const userDoc of usersSnapshot.docs) {
        const userEmail = userDoc.id

        try {
          // Get onlineTest subcollection for this user
          const onlineTestRef = collection(db, 'users', userEmail, 'onlineTest')
          const onlineTestSnapshot = await getDocs(onlineTestRef)

          // Process each test submission
          onlineTestSnapshot.docs.forEach(testDoc => {
            const testData = testDoc.data()

            allQuizResults.push({
              id: `${userEmail}_${testDoc.id}`,
              userEmail: userEmail,
              testId: testDoc.id,
              testName: testData.testName || 'Unnamed Test',
              submittedDate: testData.submitedDate || testData.submittedAt || testData.createdAt,
              totalMarks: testData.totalMarks || 'N/A',
              // Add any other fields from your data structure
              ...testData
            })
          })
        } catch (error) {
          console.log(`Could not fetch tests for user ${userEmail}:`, error.message)
          continue
        }
      }

      // Sort by submitted date (newest first)
      allQuizResults.sort((a, b) => {
        const dateA = a.submittedDate?.toDate?.() || new Date(a.submittedDate) || new Date(0)
        const dateB = b.submittedDate?.toDate?.() || new Date(b.submittedDate) || new Date(0)
        return dateB - dateA
      })

      console.log(`📝 Found ${allQuizResults.length} quiz results from ${usersSnapshot.docs.length} users`)
      return allQuizResults

    } catch (error) {
      console.error('Error loading online quiz results:', error)
      return []
    }
  }

  const deleteQuizResult = async (id) => {
    try {
      console.log('🗑️ Deleting quiz result:', id)

      // Parse the ID to get userEmail and testId
      // ID format: userEmail_testId
      const [userEmail, testId] = id.split('_')

      if (!userEmail || !testId) {
        throw new Error('Invalid quiz result ID format')
      }

      // Delete from users/{userEmail}/onlineTest/{testId}
      const testDocRef = doc(db, 'users', userEmail, 'onlineTest', testId)
      await deleteDoc(testDocRef)

      console.log(`✅ Quiz result deleted: ${userEmail}/${testId}`)
    } catch (error) {
      console.error('Error deleting quiz result:', error)
      throw error
    }
  }

  // Helper function to extract mobile number from user data
  const extractMobileNumber = (userData) => {
    // Direct field checks
    const directFields = [
      'mobile', 'phone', 'phoneNumber', 'contactNumber', 'mobileNumber',
      'Mobile', 'Phone', 'PhoneNumber', 'ContactNumber', 'MobileNumber',
      'cellphone', 'contact', 'telephone', 'tel', 'number'
    ]

    for (const field of directFields) {
      if (userData[field] && userData[field] !== '') {
        console.log(`📱 Found mobile in field: ${field} = ${userData[field]}`)
        return userData[field]
      }
    }

    // Check nested objects
    const nestedObjects = ['profile', 'personalInfo', 'contactInfo', 'details', 'userInfo']
    for (const obj of nestedObjects) {
      if (userData[obj] && typeof userData[obj] === 'object') {
        for (const field of directFields) {
          if (userData[obj][field] && userData[obj][field] !== '') {
            console.log(`📱 Found mobile in nested field: ${obj}.${field} = ${userData[obj][field]}`)
            return userData[obj][field]
          }
        }
      }
    }

    console.log(`📱 No mobile number found for user data:`, userData)
    return 'N/A'
  }

  // Get detailed quiz results with user information for a specific test
  const getTestDetailsWithUsers = async (testName) => {
    try {
      console.log(`📝 Loading detailed results for test: ${testName}`)

      // Get all users first
      const usersRef = collection(db, 'users')
      const usersSnapshot = await getDocs(usersRef)

      let testSubmissions = []

      // For each user, get their onlineTest submissions
      for (const userDoc of usersSnapshot.docs) {
        const userEmail = userDoc.id
        const userData = userDoc.data()

        console.log(`📋 Processing user: ${userEmail}`, {
          userData,
          allFields: Object.keys(userData),
          nestedStructure: JSON.stringify(userData, null, 2)
        })

        try {
          // Get onlineTest subcollection for this user
          const onlineTestRef = collection(db, 'users', userEmail, 'onlineTest')
          const onlineTestSnapshot = await getDocs(onlineTestRef)

          // Process each test submission for this specific test name
          onlineTestSnapshot.docs.forEach(testDoc => {
            const testData = testDoc.data()

            // Only include submissions for the specified test name
            if (testData.testName?.toLowerCase() === testName.toLowerCase()) {
              console.log('📱 User data for mobile debugging:', {
                userEmail,
                userData,
                availableFields: Object.keys(userData),
                mobileFields: {
                  mobile: userData.mobile,
                  phone: userData.phone,
                  phoneNumber: userData.phoneNumber,
                  contactNumber: userData.contactNumber,
                  Mobile: userData.Mobile,
                  Phone: userData.Phone
                }
              })

              testSubmissions.push({
                id: `${userEmail}_${testDoc.id}`,
                userEmail: userEmail,
                testId: testDoc.id,
                testName: testData.testName || 'Unnamed Test',
                submittedDate: testData.submitedDate || testData.submittedAt || testData.createdAt,
                totalMarks: testData.totalMarks || '0',
                // User details with extensive mobile field checking
                username: userData.name || userData.username || userData.displayName || userData.Name || 'N/A',
                email: userData.email || userData.Email || userEmail,
                mobile: extractMobileNumber(userData),
                district: userData.district || userData.location || userData.city || userData.state ||
                  userData.District || userData.Location || userData.City || userData.State || 'N/A',
                // Add any other fields from your data structure
                ...testData,
                userData: userData
              })
            }
          })
        } catch (error) {
          console.log(`Could not fetch tests for user ${userEmail}:`, error.message)
          continue
        }
      }

      // Sort by marks (highest first) to assign ranks
      testSubmissions.sort((a, b) => {
        const marksA = parseInt(a.totalMarks) || 0
        const marksB = parseInt(b.totalMarks) || 0
        return marksB - marksA
      })

      // Assign ranks
      testSubmissions.forEach((submission, index) => {
        submission.rank = index + 1
      })

      console.log(`📝 Found ${testSubmissions.length} submissions for test: ${testName}`)
      return testSubmissions

    } catch (error) {
      console.error('Error loading test details with users:', error)
      return []
    }
  }
  const syncAllTestCounts = async () => {
    try {
      console.log('🔄 Syncing testCounts for ALL home content documents...')

      const homeRef = collection(db, 'home')
      const snapshot = await getDocs(homeRef)

      let syncedCount = 0
      const results = []

      for (const homeDoc of snapshot.docs) {
        if (homeDoc.id.startsWith('mainCategory_')) {
          try {
            const actualCount = await syncTestCounts(homeDoc.id)
            results.push({ id: homeDoc.id, testCounts: actualCount, status: 'success' })
            syncedCount++
          } catch (error) {
            console.error(`Failed to sync testCounts for ${homeDoc.id}:`, error)
            results.push({ id: homeDoc.id, status: 'failed', error: error.message })
          }
        }
      }

      console.log(`✅ Synced testCounts for ${syncedCount} home content documents`)
      console.log('📊 Sync results:', results)
      return results
    } catch (error) {
      console.error('Error syncing all testCounts:', error)
      throw error
    }
  }

  // ----------------- Fix Question Types -----------------
  const fixQuestionTypesForNullImages = async (contentId = null) => {
    try {
      console.log('🔧 Starting to fix questionType for questions with null questionImageURL...')

      const results = {
        totalChecked: 0,
        totalFixed: 0,
        details: []
      }

      // Get all home content documents to iterate through
      const homeRef = collection(db, 'home')
      const homeSnapshot = await getDocs(homeRef)

      for (const homeDoc of homeSnapshot.docs) {
        // Skip if not a mainCategory document or if contentId is specified and doesn't match
        if (!homeDoc.id.startsWith('mainCategory_')) continue
        if (contentId && homeDoc.id !== contentId) continue

        const currentContentId = homeDoc.id
        console.log(`\n📂 Checking ${currentContentId}...`)

        try {
          // Get all tests for this content
          const dynamicDocRef = doc(db, 'home', currentContentId)
          const testManageRef = collection(dynamicDocRef, 'testManage')
          const testsSnapshot = await getDocs(testManageRef)

          for (const testDoc of testsSnapshot.docs) {
            const testId = testDoc.id
            console.log(`  📋 Checking test: ${testId}`)

            // Get all questions for this test
            const testDocRef = doc(testManageRef, testId)
            const questionSetRef = collection(testDocRef, 'questionSet')
            const questionsSnapshot = await getDocs(questionSetRef)

            // Use batch for efficient updates
            const batch = writeBatch(db)
            let batchCount = 0

            for (const questionDoc of questionsSnapshot.docs) {
              const questionData = questionDoc.data()
              results.totalChecked++

              // Check if questionImageURL is null and questionType is "image"
              if (questionData.questionImageURL === null && questionData.questionType === 'image') {
                console.log(`    ✏️  Fixing question: ${questionDoc.id} (changing questionType from "image" to "text")`)

                // Update the question to set questionType to "text"
                batch.update(questionDoc.ref, {
                  questionType: 'text',
                  updatedAt: new Date()
                })

                batchCount++
                results.totalFixed++

                results.details.push({
                  contentId: currentContentId,
                  testId: testId,
                  questionId: questionDoc.id,
                  status: 'fixed'
                })

                // Commit batch every 500 operations (Firestore limit)
                if (batchCount >= 500) {
                  await batch.commit()
                  console.log(`    ✅ Committed batch of ${batchCount} updates`)
                  batchCount = 0
                }
              }
            }

            // Commit any remaining updates in the batch
            if (batchCount > 0) {
              await batch.commit()
              console.log(`    ✅ Committed final batch of ${batchCount} updates for test ${testId}`)
            }
          }

          // Clear cache for this content to ensure fresh data
          clearTestCache(currentContentId)

        } catch (error) {
          console.error(`❌ Error processing ${currentContentId}:`, error)
          results.details.push({
            contentId: currentContentId,
            status: 'error',
            error: error.message
          })
        }
      }

      console.log('\n✅ Fix completed!')
      console.log(`📊 Total questions checked: ${results.totalChecked}`)
      console.log(`📊 Total questions fixed: ${results.totalFixed}`)

      return results

    } catch (error) {
      console.error('❌ Error fixing question types:', error)
      throw error
    }
  }

  // ----------------- Subscription Functions -----------------
  /* ----------------- Subscription Functions ----------------- */

  // Helper to check if an ID is a new-style card ID
  const isCardId = (id) => typeof id === 'string' && id.startsWith('card-')

  const getSubscriptions = async () => {
    try {
      // 1. Fetch legacy subscriptions from 'Subscription' collection
      const subscriptionsRef = collection(db, 'Subscription')
      const subSnapshot = await getDocs(subscriptionsRef)
      const legacySubs = await Promise.all(subSnapshot.docs.map(async (subDoc) => {
        const data = subDoc.data()

        // Populate actual cards from cardIds if they exist
        // Populate actual cards from cardIds if they exist
        let populatedCards = []

        if (data.cardIds && data.cardIds.length > 0) {
          try {
            // Use Promise.all to fetch docs individually to avoid 'in' query limit of 10
            const cardsPromises = data.cardIds.map(async (cid) => {
              // Trim just in case
              const cleanId = typeof cid === 'string' ? cid.trim() : cid
              const d = await getDoc(doc(db, 'home', cleanId))
              return d.exists() ? { id: d.id, ...d.data() } : null
            })

            const results = await Promise.all(cardsPromises)

            populatedCards = results.filter(c => c !== null).map(d => ({
              id: d.id,
              ...d,
              // Ensure UI compatible fields
              title: d.CollectionName || d.title || d.id,
              TestCount: d.TestCount || d.testCount || '0'
            }))

          } catch (err) {
            console.error('Error fetching linked cards:', err)
          }
        }
        // Fallback to legacy 'cards' array if no cardIds
        else if (data.cards && data.cards.length > 0) {
          populatedCards = data.cards
        }

        return {
          id: subDoc.id,
          collectionName: subDoc.id,
          CollectionName: data.CollectionName || subDoc.id,
          SubscriptionAmount: data.SubscriptionAmount || '',
          collectionImageUrl: data.collectionImageUrl || '',
          cardsCount: populatedCards.length,
          cards: populatedCards,
          cardIds: data.cardIds || [],
          index: (data.index !== undefined && data.index !== null) ? Number(data.index) : undefined,
          createdAt: data.createdAt,
          discountPlan: data.discountPlan || '',
          source: 'Subscription'
        }
      }))

      return legacySubs
    } catch (error) {
      console.error('Error getting subscriptions:', error)
      throw error
    }
  }

  // Helper to determine subscription plan status based on amount
  const getSubscriptionPlanStatus = (amount) => {
    const amountStr = String(amount || '').trim()
    return (amountStr && amountStr !== '0' && amountStr.toLowerCase() !== 'free') ? 'paid' : 'free'
  }

  /* 
    DEPRECATED: addSubscription now strictly adds to 'Subscription' collection.
    Use addCardToSubscription for adding cards.
  */
  const addSubscription = async (data) => {
    try {
      // Get next index from meta/subscriptions document
      const metaRef = doc(db, 'meta', 'subscriptions')
      const metaSnap = await getDoc(metaRef)
      let nextIndex = 1

      if (metaSnap.exists()) {
        nextIndex = (metaSnap.data().lastIndex || 0) + 1
      }

      // Allow manual override
      const finalIndex = (data.index !== undefined && data.index !== null) ? Number(data.index) : nextIndex

      const subscriptionsRef = doc(db, 'Subscription', data.collectionName)
      const amountStr = data.amount?.toString() || ''
      const planStatus = getSubscriptionPlanStatus(amountStr)

      await setDoc(subscriptionsRef, {
        CollectionName: data.collectionName || '',
        SubscriptionAmount: amountStr,
        subscriptionPlanStatus: planStatus,
        collectionImageUrl: data.imageUrl || '',
        discountPlan: data.discountPlan || '',
        cardsCount: 0,
        cardIds: [], // Initialize empty cardIds
        createdAt: new Date(),
        index: finalIndex
      })

      console.log(`✅ Subscription added with index: ${finalIndex}`)

      // Update the meta/subscriptions document based on all existing subscriptions
      await syncSubscriptionMeta()

      return data.collectionName
    } catch (error) {
      console.error('Error adding subscription:', error)
      throw error
    }
  }

  // Automatically number all existing subscriptions that lack an index
  const bulkSyncSubscriptionIndices = async () => {
    try {
      const subscriptionsRef = collection(db, 'Subscription')
      const snapshot = await getDocs(subscriptionsRef)

      const subs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

      // Sort by creation date or ID
      subs.sort((a, b) => {
        const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0)
        const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0)
        return timeA - timeB
      })

      const batch = writeBatch(db)
      subs.forEach((sub, i) => {
        const ref = doc(db, 'Subscription', sub.id)
        batch.update(ref, { index: i + 1 })
      })

      await batch.commit()
      await syncSubscriptionMeta()
      console.log(`✅ Bulk synced ${subs.length} subscription indices`)
      return true
    } catch (error) {
      console.error('Error bulk syncing indices:', error)
      throw error
    }
  }

  // Helper to sync the subscription meta tracker with actual database state
  const syncSubscriptionMeta = async () => {
    try {
      const subscriptionsRef = collection(db, 'Subscription')
      const snapshot = await getDocs(subscriptionsRef)

      let maxIndex = 0
      snapshot.docs.forEach(doc => {
        const idx = Number(doc.data().index)
        if (!isNaN(idx) && idx > maxIndex) {
          maxIndex = idx
        }
      })

      const metaRef = doc(db, 'meta', 'subscriptions')
      await setDoc(metaRef, { lastIndex: maxIndex }, { merge: true })
      console.log(`🔄 Synced subscription meta tracker to: ${maxIndex}`)
      return maxIndex
    } catch (error) {
      console.error('Error syncing subscription meta:', error)
    }
  }

  const updateSubscription = async (id, data) => {
    try {
      // Determine collection based on ID format
      const isNewStyle = isCardId(id)
      const collectionName = isNewStyle ? 'home' : 'Subscription'
      const subscriptionRef = doc(db, collectionName, id)

      // Check if document exists first
      const docSnap = await getDoc(subscriptionRef)
      const docExists = docSnap.exists()

      // Check for old image to delete from Storage if being replaced
      if (data.imageUrl !== undefined && docExists) {
        try {
          const currentData = docSnap.data()
          const oldImageUrl = currentData.collectionImageUrl

          // Delete old image if it exists and is different from new one
          if (oldImageUrl && oldImageUrl !== data.imageUrl && oldImageUrl.includes('firebase')) {
            console.log('🗑️ Deleting old subscription image from Storage:', oldImageUrl)
            try {
              await deleteImage(oldImageUrl)
              console.log('✅ Old subscription image deleted from Storage')
            } catch (deleteError) {
              console.warn('⚠️ Could not delete old subscription image (may not exist):', deleteError.message)
            }
          }
        } catch (error) {
          console.warn('⚠️ Could not check for old subscription image:', error.message)
        }
      }

      const updateData = {}

      if (data.collectionName) updateData.CollectionName = data.collectionName
      if (data.amount !== undefined) {
        updateData.SubscriptionAmount = data.amount.toString()
        // Auto-set planStatus based on amount
        updateData.subscriptionPlanStatus = getSubscriptionPlanStatus(data.amount.toString())
      }
      if (data.imageUrl !== undefined) updateData.collectionImageUrl = data.imageUrl
      if (data.cardsCount !== undefined) updateData.cardsCount = data.cardsCount
      if (data.cards !== undefined) updateData.cards = data.cards
      if (data.discountPlan !== undefined) updateData.discountPlan = data.discountPlan
      if (data.index !== undefined) updateData.index = Number(data.index)

      // Use setDoc with merge to create document if it doesn't exist
      if (docExists) {
        await updateDoc(subscriptionRef, updateData)
        console.log(`✅ Subscription updated in Firestore (${collectionName})`)

        // If index was updated, sync the meta tracker
        if (data.index !== undefined) {
          await syncSubscriptionMeta()
        }
      } else {
        // Document doesn't exist, create it with the ID and basic fields
        // Note: For 'home' collection, we should probably be careful about creating arbitrary docs if they don't exist
        // But for 'update', we assume it might be a create-or-update operation
        await setDoc(subscriptionRef, {
          id: id,
          CollectionName: data.collectionName || id,
          SubscriptionAmount: data.amount?.toString() || '0',
          subscriptionPlanStatus: getSubscriptionPlanStatus(data.amount?.toString() || '0'),
          collectionImageUrl: data.imageUrl || '',
          cardsCount: data.cardsCount || 0,
          cards: data.cards || [],
          createdAt: new Date(),
          ...updateData
        })
        console.log(`✅ Subscription created in Firestore (${collectionName})`)
      }
    } catch (error) {
      console.error('Error updating subscription:', error)
      throw error
    }
  }

  const deleteSubscription = async (id) => {
    try {
      console.log('🗑️ Deleting subscription from Firestore:', id)

      const isNewStyle = isCardId(id)
      const collectionName = isNewStyle ? 'home' : 'Subscription'
      const subscriptionRef = doc(db, collectionName, id)

      // Get the document to check for Storage files
      const docSnap = await getDoc(subscriptionRef)
      if (docSnap.exists()) {
        const data = docSnap.data()

        // Delete subscription image from Storage if it exists
        const imageUrl = data.collectionImageUrl || data.imageUrl
        if (imageUrl && imageUrl.includes('firebase')) {
          console.log('🗑️ Deleting subscription image from Storage:', imageUrl)
          try {
            await deleteImage(imageUrl)
            console.log('✅ Subscription image deleted from Storage')
          } catch (deleteError) {
            console.warn('⚠️ Could not delete subscription image (may not exist):', deleteError.message)
          }
        }

        // 2. Delete linked cards from 'home' collection (New Architecture)
        const cardIds = data.cardIds || []
        if (cardIds.length > 0) {
          console.log(`🗑️ Deleting ${cardIds.length} linked cards from home collection...`)

          for (const cardId of cardIds) {
            try {
              // Fetch card details for image cleanup
              const cardRef = doc(db, 'home', cardId)
              const cardSnap = await getDoc(cardRef)

              if (cardSnap.exists()) {
                const cardData = cardSnap.data()
                // Delete card image if it exists
                if (cardData.imageUrl && cardData.imageUrl.includes('firebase')) {
                  console.log(`🗑️ Deleting image for card ${cardId}:`, cardData.imageUrl)
                  try {
                    await deleteImage(cardData.imageUrl)
                  } catch (e) {
                    console.warn(`⚠️ Could not delete image for ${cardId}:`, e.message)
                  }
                }
              }

              // Perform cascade delete of the home document (tests, questions, subcollections)
              await deleteHomeContentCascade(cardId)
              console.log(`✅ Cascade deleted linked card: ${cardId}`)
            } catch (cardErr) {
              console.error(`❌ Error deleting linked card ${cardId}:`, cardErr)
            }
          }
        }

        // 3. Delete embedded cards images (Legacy Compatibility)
        const cards = data.cards || []
        if (cards.length > 0) {
          console.log(`🗑️ Deleting ${cards.length} legacy embedded card images...`)
          for (const card of cards) {
            if (card.imageUrl && card.imageUrl.includes('firebase')) {
              try {
                await deleteImage(card.imageUrl)
              } catch (e) {
                console.warn(`⚠️ Could not delete legacy image:`, e.message)
              }
            }
          }
        }
      }

      // 4. Delete the Firestore document
      await deleteDoc(subscriptionRef)
      console.log(`✅ Subscription package deleted from Firestore (${collectionName})`)

      // Update the meta/subscriptions document based on remaining subscriptions
      await syncSubscriptionMeta()
    } catch (error) {
      console.error('Error deleting subscription:', error)
      throw error
    }
  }

  const getSubscriptionById = async (id) => {
    try {
      const subscriptionRef = doc(db, 'Subscription', id)
      const snapshot = await getDoc(subscriptionRef)
      if (snapshot.exists()) {
        const data = snapshot.data()

        // Populate actual cards from cardIds
        // Populate actual cards from cardIds
        let populatedCards = []

        if (data.cardIds && data.cardIds.length > 0) {
          try {
            // Fetch all linked cards in parallel
            const cardPromises = data.cardIds.map(cardId => {
              const cleanId = typeof cardId === 'string' ? cardId.trim() : cardId
              return getDoc(doc(db, 'home', cleanId))
            })
            const cardSnapshots = await Promise.all(cardPromises)

            const fetchedCards = cardSnapshots
              .filter(s => s.exists())
              .map(d => ({
                id: d.id,
                ...d.data(),
                // UI Mapping
                title: d.data().CollectionName || d.data().title || d.id,
                TestCount: d.data().TestCount || d.data().testCount || '0'
              }))

            populatedCards = fetchedCards
          } catch (err) {
            console.error('Error fetching linked cards details:', err)
          }
        }
        // Fallback to legacy
        else if (data.cards && data.cards.length > 0) {
          populatedCards = data.cards
        }

        return {
          id: snapshot.id,
          collectionName: data.CollectionName || snapshot.id,
          amount: data.SubscriptionAmount || '',
          imageUrl: data.collectionImageUrl || '',
          cardsCount: populatedCards.length,
          cards: populatedCards,
          cardIds: data.cardIds || []
        }
      }
      return null
    } catch (error) {
      console.error('Error getting subscription:', error)
      throw error
    }
  }

  // Create a new card in 'home' and link it to the Subscription
  const createSubscriptionCard = async (subscriptionId, cardData) => {
    try {
      console.log(`Creating card for subscription ${subscriptionId}...`)

      // 1. Generate new card ID (card-N)
      const homeRef = collection(db, 'home')
      const snapshot = await getDocs(homeRef)
      let maxId = 0
      snapshot.docs.forEach(doc => {
        if (isCardId(doc.id)) {
          const numPart = parseInt(doc.id.split('-')[1])
          if (!isNaN(numPart) && numPart > maxId) maxId = numPart
        }
      })
      const newCardId = `card-${maxId + 1}`
      const newCardRef = doc(db, 'home', newCardId)

      // 2. Prepare card data
      // If copying from home content, we need to fetch that first
      let contentToSave = {
        id: newCardId,
        CollectionName: cardData.title, // Standardize on CollectionName for home docs
        title: cardData.title,
        TestCount: cardData.TestCount || '0',
        imageUrl: cardData.imageUrl || '',
        discountPlan: cardData.discountPlan || '',
        index: cardData.index || undefined,
        createdAt: new Date(),
        type: 'subscription_card',
        linkedSubscriptionId: subscriptionId
      }

      if (cardData.homeContentId) {
        const sourceRef = doc(db, 'home', cardData.homeContentId)
        const sourceSnap = await getDoc(sourceRef)
        if (sourceSnap.exists()) {
          const sourceData = sourceSnap.data()
          // eslint-disable-next-line no-unused-vars
          const { plan, planStatus, ...safeSourceData } = sourceData
          contentToSave = {
            ...contentToSave,
            ...safeSourceData, // Merge source data
            // Ensure critical fields aren't overwritten by source defaults if different
            CollectionName: cardData.title,
            title: cardData.title,
            imageUrl: cardData.imageUrl || sourceData.imageUrl || ''
          }
        }
      }

      // 3. Save to home/card-N
      await setDoc(newCardRef, contentToSave)
      console.log(`✅ Created card document: home/${newCardId}`)

      // 4. Update metadata if index is provided
      if (cardData.index) {
        const metaRef = doc(db, 'meta', 'homeContent')
        const metaSnap = await getDoc(metaRef)
        const currentLastIndex = metaSnap.exists() ? (metaSnap.data().lastIndex || 0) : 0
        const newIndex = parseInt(cardData.index)

        if (newIndex > currentLastIndex) {
          await setDoc(metaRef, {
            lastIndex: newIndex,
            lastUpdated: new Date()
          }, { merge: true })
        }
      }

      // 5. Copy subcollections if source exists
      if (cardData.homeContentId) {
        await copySubCollections(cardData.homeContentId, newCardId, cardData.sourceCollection || 'home')
      }

      // 6. Link in Subscription/subscriptionId
      const subRef = doc(db, 'Subscription', subscriptionId)
      await updateDoc(subRef, {
        cardIds: arrayUnion(newCardId),
        cardsCount: (cardData.currentCardCount || 0) + 1
      })

      return newCardId
    } catch (error) {
      console.error('Error creating linked subscription card:', error)
      throw error
    }
  }

  const removeSubscriptionCard = async (subscriptionId, cardId) => {
    try {
      console.log(`🗑️ Removing card ${cardId} from subscription ${subscriptionId} (Unlinking only)`)

      // 1. Get Subscription doc to check existing cards
      const subRef = doc(db, 'Subscription', subscriptionId)
      const subSnap = await getDoc(subRef)

      if (subSnap.exists()) {
        const data = subSnap.data()
        const cardIds = data.cardIds || []

        // 2. Remove from cardIds array
        const updatedCardIds = cardIds.filter(id => id !== cardId)
        await updateDoc(subRef, {
          cardIds: updatedCardIds,
          cardsCount: updatedCardIds.length
        })
        console.log(`✅ Successfully unlinked card ${cardId} from subscription ${subscriptionId}`)
        return true
      } else {
        console.warn(`⚠️ Subscription ${subscriptionId} not found`)
        return false
      }
    } catch (error) {
      console.error('Error removing subscription card:', error)
      throw error
    }
  }

  const updateSubscriptionCard = async (cardId, cardData) => {
    try {
      console.log(`Updating card: ${cardId}`)
      const cardRef = doc(db, 'home', cardId)

      const updatePayload = {
        title: cardData.title,
        CollectionName: cardData.title,
        imageUrl: cardData.imageUrl,
        discountPlan: cardData.discountPlan || '',
        updatedAt: new Date()
      }

      await updateDoc(cardRef, updatePayload)
      console.log(`✅ Card updated: home/${cardId}`)
      return true
    } catch (error) {
      console.error('Error updating subscription card:', error)
      throw error
    }
  }

  // Helper to deep copy testManage subcollection from one doc to another
  const copySubCollections = async (sourceId, targetId, sourceCollection = 'home') => {
    try {
      const sourceTestRef = collection(db, sourceCollection, sourceId, 'testManage')
      const sourceTestSnap = await getDocs(sourceTestRef)

      for (const testDoc of sourceTestSnap.docs) {
        const testData = testDoc.data()
        const targetTestRef = doc(db, 'home', targetId, 'testManage', testDoc.id)
        await setDoc(targetTestRef, testData)

        const sourceQRef = collection(db, 'home', sourceId, 'testManage', testDoc.id, 'questionSet')
        const sourceQSnap = await getDocs(sourceQRef)

        for (const qDoc of sourceQSnap.docs) {
          await setDoc(
            doc(db, 'home', targetId, 'testManage', testDoc.id, 'questionSet', qDoc.id),
            qDoc.data()
          )
        }
      }
      console.log(`✅ Copied subcollections from ${sourceId} to ${targetId}`)
    } catch (err) {
      console.error('Error copying subcollections:', err)
    }
  }

  // Copy home content with all subcollections to subscription
  const copyHomeContentToSubscription = async (homeContentId, subscriptionId, cardTitle) => {
    try {
      console.log(`📋 Copying home content ${homeContentId} to subscription ${subscriptionId}`)

      // Get the home content document
      const homeRef = doc(db, 'home', homeContentId)
      const homeSnap = await getDoc(homeRef)

      if (!homeSnap.exists()) {
        console.error('Home content not found:', homeContentId)
        return
      }

      const homeData = homeSnap.data()

      // Strip plan-related fields when copying into subscription card
      // eslint-disable-next-line no-unused-vars
      const { plan, planStatus, ...safeHomeData } = homeData || {}

      // Create the card document in Subscription (or home/card-N) with sanitized home content data
      const isNewStyle = isCardId(subscriptionId)
      const collectionName = isNewStyle ? 'home' : 'Subscription'
      const cardRef = doc(db, collectionName, subscriptionId, 'cards', cardTitle)
      await setDoc(cardRef, {
        ...safeHomeData,
        copiedFrom: homeContentId,
        copiedAt: new Date()
      })

      // Copy testManage subcollection
      const testManageRef = collection(db, 'home', homeContentId, 'testManage')
      const testManageSnap = await getDocs(testManageRef)

      for (const testDoc of testManageSnap.docs) {
        const testData = testDoc.data()
        const targetTestRef = doc(db, collectionName, subscriptionId, 'cards', cardTitle, 'testManage', testDoc.id)
        await setDoc(targetTestRef, testData)

        // Copy questionSet subcollection for each test
        const questionSetRef = collection(db, 'home', homeContentId, 'testManage', testDoc.id, 'questionSet')
        const questionSetSnap = await getDocs(questionSetRef)

        for (const questionDoc of questionSetSnap.docs) {
          const questionData = questionDoc.data()
          const targetQuestionRef = doc(db, collectionName, subscriptionId, 'cards', cardTitle, 'testManage', testDoc.id, 'questionSet', questionDoc.id)
          await setDoc(targetQuestionRef, questionData)
        }

        console.log(`  ✓ Copied test: ${testDoc.id} with ${questionSetSnap.size} questions`)
      }

      console.log(`✅ Successfully copied ${testManageSnap.size} tests with all questions`)
      return true
    } catch (error) {
      console.error('Error copying home content to subscription:', error)
      throw error
    }
  }

  const getSubscriptionMeta = async () => {
    try {
      const metaRef = doc(db, 'meta', 'subscriptions')
      const snapshot = await getDoc(metaRef)
      return snapshot.exists() ? snapshot.data() : null
    } catch (error) {
      console.error('Error getting subscription meta:', error)
      throw error
    }
  }

  // ----------------- Value Export -----------------
  const value = {
    // firebase status
    isFirebaseReady,

    // storage
    uploadImage,
    uploadFile,
    deleteImage,

    // generic firestore helpers
    getDocuments,
    getDocumentById,
    addDocument,
    updateDocument,
    deleteDocument,

    // subscriptions
    getSubscriptions,
    addSubscription,
    updateSubscription,
    deleteSubscription,
    getSubscriptionById,
    syncSubscriptionMeta,
    bulkSyncSubscriptionIndices,
    copyHomeContentToSubscription, // Kept for legacy if needed, but createSubscriptionCard is preferred
    createSubscriptionCard,
    updateSubscriptionCard,
    removeSubscriptionCard,
    getSubscriptionMeta,

    // home content
    getHomeContents,
    addHomeContent,
    updateHomeContent,
    deleteHomeContent,
    deleteHomeContentCascade,
    syncTestCounts,
    syncAllTestCounts,
    fixQuestionTypesForNullImages,
    getHomeContentMeta,
    getHomeContentById,
    bulkSyncHomeContentIndices,
    createHomeContentCard,
    updateHomeContentCard,
    removeHomeContentCard,
    addCardDirectly,
    updateCardDirectly,

    // nursing bullets
    getNursingBullets,
    addNursingBullet,
    updateNursingBullet,
    deleteBullet,

    // questions / categories / users / results
    getQuestions,
    getQuestion,
    addQuestion,
    updateQuestion,
    deleteQuestion,

    getCategories,
    getCategory,
    addCategory,
    updateCategory,
    deleteCategory,

    getUsers,
    getReports,
    deleteReport,
    getUser,
    createUser,
    updateUser,
    deleteUser,
    getAllUserPurchases,
    getResults,

    // test questions
    getTestQuestions,
    addTestQuestions,
    createTestMetadata,
    updateTestMetadata,
    updateTestById,
    updateTestWithQuestions,
    deleteTestQuestions,
    clearTestCache,

    // online tests
    getOnlineTests,
    addOnlineTest,
    deleteTest,
    updateTestQuestions,
    addOnlineTestQuestions,
    uploadQuestionImage,
    getOnlineTestQuestions,
    updateOnlineTestQuestion,
    deleteOnlineTestQuestion,
    deleteOnlineTestQuestions,

    // realtime helpers (generic)
    fetchRealtimeData,
    addRealtimeData,
    updateRealtimeData,
    deleteRealtimeData,

    // updates (firestore)
    getUpdates,
    addUpdate,
    updateUpdate,
    deleteUpdate,
    sendUpdate,
    dismissUpdate,

    // alerts (realtime)
    getAlerts,
    addAlert,
    updateAlert,
    deleteAlert,
    sendAlert,
    dismissAlert,
    addAlertToFirestore,
    addAlertBoth,
    addAlertSubnote,
    getAlertSubnotes,
    updateAlertSubnote,
    deleteAlertSubnote,
    testSubnoteDataIntegrity,
    testFirestoreConnection,

    // leaderboard
    getLeaderboard,
    updateLeaderboard,
    deleteFromLeaderboard,

    // users
    getUserDetails,
    getUserTestResults,

    // quiz results  
    getOnlineQuizResults,
    getTestDetailsWithUsers,
    deleteQuizResult,

    // analytics
    getAnalytics
  }

  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>
}

export { FirebaseProvider, useFirebase }
export default FirebaseProvider
